import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DEFAULT_FROM_NUMBER } from './demo/testHelpers';
import useDialer from '../softphone/useDialer';
import QRCode from 'qrcode';
import { proposeUpdate } from './p3TripUpdates';
import { parseLpa, PUBLIC_TEST_LPA } from './p3Esim';
import { getTravelApiBaseUrl, getLiveTranscriptWsUrl } from '../lib/apiConfig';
import { authHeaders, sessionToken, setSessionToken } from './p3Session';
import { PayProvider, useBilling, usePay, CardPicker, cents, payReady } from './P3Pay';
import {
  LANGUAGES, languageOf, t, tEn, loadLocale, initialLocale, locale, dir,
  formatDate, formatDateTime, formatNumber, relativeDay, languageName,
} from '../i18n';

/*
 * P3 Travel Pass test bench (route #/p3-testbench).
 * One long page: P2 companion tools plus P3 voice and calling, against the V2 server.
 * Needs a signed-in, verified Freshroad Travel Pass account (#/p3-login, #/p3-signup); without one it goes to log-in.
 * State is kept in this browser (localStorage) and mirrored to the travel server, split by owner: trips (Travel),
 * account state (eSIM, number, credits), the phone log (your own calls and texts) and Milo's conversations.
 * See backend/travel/p3Accounts.js and schema.js.
 * UI text comes from src/i18n (26 languages); values sent to the AI or stored stay English ids.
 */

const STATE_KEY = 'p3_testbench_state_v2';

// [group id, tab icon, section ids]; titles come from group.<id> / title.<section>.
const GROUPS = [
  ['today', '◉', ['today']],
  ['companion', '✦', ['chat']],
  ['mytrip', '▣', ['trip', 'plan', 'bookings']],
  ['tools', '◫', ['esim', 'number', 'translate', 'hotel', 'local', 'ready', 'food']],
  ['calls', '☎', ['call', 'results', 'dialer', 'status']],
];
const groupOf = (id) => GROUPS.find((g) => g[2].includes(id))?.[0] || 'today';

// Unsplash photos (free license, hotlinking allowed), picked by destination keyword.
const PHOTO = (id) => `https://images.unsplash.com/${id}?w=1200&q=70&auto=format&fit=crop`;
const PHOTOS = [
  [/kyoto|osaka|nara/i, 'photo-1545569341-9eb8b30979d9'],
  [/tokyo|japan/i, 'photo-1542051841857-5f90071e7989'],
  [/seoul|korea|busan/i, 'photo-1546874177-9e664107314e'],
  [/bangkok|thailand|phuket|chiang/i, 'photo-1508009603885-50cf7c579365'],
  [/singapore/i, 'photo-1525625293386-3f8f99389edd'],
  [/shanghai|beijing|china/i, 'photo-1538428494232-9c0d8a3ab403'],
  [/hong ?kong|macau/i, 'photo-1536599018102-9f803c140fc1'],
  [/bali|indonesia|jakarta/i, 'photo-1577717903315-1691ae25ab3f'],
];
const DEFAULT_PHOTO = 'photo-1488085061387-422e29b40080';
const photoFor = (destination) => PHOTO((PHOTOS.find(([re]) => re.test(destination || '')) || [])[1] || DEFAULT_PHOTO);

// Languages offered as translation / staff targets, named in the current UI language.
const targetLanguages = () => LANGUAGES.map((l) => [l.code, languageName(l.code)]);

// Internal dev checklist: kept in English on purpose.
const STATUS = [
  ['Companion chat with trip context', 'done', 'P2 / UX 03. Multi-intent replies; call requests open an inline call review.'],
  ['Itinerary plan, replan a day, undo', 'done', 'PLAN 01. Confirmed bookings are passed in and kept.'],
  ['Booking import (text or screenshot)', 'done', 'TRIP 01. Uncertain fields flagged; duplicates by confirmation number.'],
  ['Text and photo translation', 'done', 'TOOLS 02. Low-confidence blocks marked.'],
  ['Ask your hotel (message + speaker + call)', 'done', 'HOTEL 01. Speaker uses the browser voice.'],
  ['Local help (dining, shopping, explore, urgent)', 'done', 'TOOLS 03. No live data: results say what to verify.'],
  ['Travel Ready checklist', 'done', 'READY 01. Not grounded in official entry sources yet.'],
  ['Meal photo calorie estimate', 'done', 'Portion control recalculates locally.'],
  ['AI call from plain instructions', 'done', 'CALL 01. Uses your trip (hotel phone, name, bookings). Used by AI Calling, chat and hotel.'],
  ['Call outcome + save to My Trip', 'done', 'CALL 03–04. Confirmed / declined / no answer / needs approval / uncertain, from Twilio status + transcript.'],
  ['AI conversation log (p3_conversations)', 'done', 'Chat, voice, calls and tools stored with traveler, trip, messages, call data and timings. Sample traveler/trip ids for now.'],
  ['26-language app UI (P1 catalog)', 'done', 'UX 05. All bench text in 26 languages, RTL for Arabic/Persian/Urdu, locale dates and numbers. Machine-translated: needs native review.'],
  ['AI replies in the app language', 'done', 'Companion tools, call plans and call results answer in the UI language; call language stays separate.'],
  ['Business number lookup', 'done', 'No number given → OpenAI web search finds the official one, shown with its source to check before dialing.'],
  ['Call script reaches the AI + call rules', 'done', 'Text Prompt scripts are now inlined on every call path; P3 calls add caller rules, no commitments, [HANGUP] when done.'],
  ['International dialing (+81, +86, …)', 'done', 'Outbound keeps the country code on numbers that start with +.'],
  ['Spoken script in the call language', 'done', 'Planner writes the opening line and instructions in the target language.'],
  ['Live call transcript', 'done', 'Streams from /live-transcript while the call runs.'],
  ['Voicemail / no-answer detection', 'done', 'Twilio async AMD hangs up on machines.'],
  ['Voice chat with Milo (browser)', 'done', 'Talk to Milo knows your trip, preferences, bookings and itinerary.'],
  ['Inline chat call review', 'partial', 'CHATCALL 01. Works here; no shared server-side call task or approval record.'],
  ['Trip memory and preferences', 'partial', 'MEM 01. This browser only; no server memory, sources or sync.'],
  ['Call languages vs the PRD’s 26', 'partial', 'No voice for Bangla, Urdu, Persian, Swahili, Burmese, Khmer, Lao. STT per language untested.'],
  ['Server error messages', 'partial', 'Errors from the V2 server are still English.'],
  ['Inbound calls in the browser (softphone)', 'partial', 'Rings dashboard users; not tied to a per-user Travel Number.'],
  ['Push-to-talk voice translation', 'missing', 'TOOLS 02. Voice chat exists; translate-what-I-say does not.'],
  ['Maps, routes and fares', 'missing', 'TOOLS 01. No maps provider wired in.'],
  ['Offline saved essentials', 'missing', 'TRIP 01. No offline cache.'],
  ['Caller ID = user’s own Travel Number', 'missing', 'CALL 02. Calls use the shared test number.'],
  ['Travel Number purchase / verification / expiry', 'missing', 'LINE 01, LINE 05.'],
  ['Manual in-app dialer (outbound WebRTC)', 'done', 'LINE 03. Server starts the call from the app’s own number (P3_PHONE_NUMBER); no TwiML App needed.'],
  ['SMS threads and delivery state', 'missing', 'LINE 04. No inbound SMS webhook.'],
  ['Pause mid-call for fee / deposit approval', 'missing', 'CALL 02. The agent is told to refuse and defer.'],
  ['Credits reserve → settle', 'missing', 'BILL 02. Nothing is charged yet.'],
];

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.tb { --bg:#e9edf2; --surface:#f7f8fa; --card:#fff; --ink:#17263d; --muted:#637188; --faint:#89919e; --line:#e6e9ef;
  --navy:#17263d; --cream:#ffe9c3; --beige:#f5ead6; --brown:#765b30; --gold:#b98a3e; --gold-ink:#8b6730;
  --blue:#245bd6; --blue-bg:#edf3ff; --ok:#1d7a42; --ok-bg:#e2f3e8; --mid:#8a5a00; --mid-bg:#fbefd5; --bad:#a32727; --bad-bg:#fbe2e2;
  min-height:100vh; background:var(--bg); color:var(--ink);
  font:16px/1.5 Inter,-apple-system,BlinkMacSystemFont,"Segoe UI","PingFang SC",sans-serif; }
.tb *{ box-sizing:border-box; }
.tb button{ text-transform:none; letter-spacing:normal; font-family:inherit; }
.tb-layout{ display:grid; grid-template-columns:230px minmax(0,880px); justify-content:center; gap:32px; padding:24px 16px 0; align-items:start; }
.tb-side{ position:sticky; top:24px; max-height:calc(100vh - 48px); overflow-y:auto; padding:8px 4px; }
.tb-side .tb-brand{ font-size:12px; font-weight:800; letter-spacing:.08em; color:var(--gold-ink); margin:0 10px 14px; }
.tb-side h4{ margin:14px 10px 6px; font-size:11px; letter-spacing:.08em; text-transform:uppercase; color:var(--faint); }
.tb-side a{ display:block; padding:7px 12px; border-inline-start:3px solid transparent; border-start-end-radius:10px; border-end-end-radius:10px; color:var(--ink); text-decoration:none; font-size:14px; transition:background .15s,border-color .15s; }
.tb-side a:hover{ background:rgba(255,255,255,.6); }
.tb-side a[aria-current="true"]{ border-inline-start-color:var(--gold); background:#fff; font-weight:650; }
.tb-phone{ background:var(--surface); border-radius:28px 28px 0 0; box-shadow:0 0 60px rgba(21,38,61,.08); min-height:calc(100vh - 24px); position:relative; }
.tb-head{ position:sticky; top:0; z-index:20; display:flex; align-items:center; gap:10px; padding:18px 20px 12px; background:rgba(247,248,250,.92); backdrop-filter:blur(10px); border-radius:28px 28px 0 0; }
.tb-head .tb-k{ font-size:11px; font-weight:800; letter-spacing:.08em; color:var(--gold-ink); }
.tb-head h1{ margin:0; font-size:22px; font-weight:700; color:var(--ink); line-height:1.2; }
.tb-head .tb-title{ display:inline-block; animation:tbFade .35s ease; }
.tb-pill{ margin-inline-start:auto; font-size:12px; font-weight:600; padding:6px 12px; border-radius:999px; background:var(--blue-bg); color:var(--blue); white-space:nowrap; max-width:46%; overflow:hidden; text-overflow:ellipsis; }
.tb-body{ padding:4px 24px 110px; display:grid; gap:16px; }
.tb-group{ margin:14px 4px -4px; scroll-margin-top:84px; }
.tb-group h2{ color:var(--ink); margin:0; font-size:20px; font-weight:750; }
.tb-group p{ margin:2px 0 0; color:var(--muted); font-size:14px; }
.tb-card{ background:var(--card); border:1px solid var(--line); border-radius:18px; padding:18px; scroll-margin-top:84px; }
.tb-card > header{ margin-bottom:12px; }
.tb-kicker{ font-size:11px; font-weight:800; letter-spacing:.08em; text-transform:uppercase; color:var(--gold-ink); }
.tb-card h3{ color:var(--ink); margin:2px 0 4px; font-size:18px; font-weight:700; }
.tb-card > header p{ margin:0; color:var(--muted); font-size:14px; }
.tb-sub{ margin:18px 0 6px; font-size:14px; font-weight:700; }
.tb-label{ display:block; font-size:13px; font-weight:600; margin:12px 0 5px; color:#3a4860; }
.tb-input{ width:100%; min-height:44px; padding:10px 12px; border:1px solid #d8dde6; border-radius:12px; font:inherit; font-size:16px; background:#fff; color:var(--ink); transition:border-color .15s, box-shadow .15s; }
.tb-input:focus{ outline:none; border-color:var(--gold); box-shadow:0 0 0 3px rgba(185,138,62,.18); }
textarea.tb-input{ resize:vertical; }
.tb-row{ display:flex; gap:10px; flex-wrap:wrap; align-items:flex-end; }
.tb-row > .tb-f{ flex:1 1 180px; min-width:0; }
.tb-btn,.tb-ghost,.tb-gold,.tb-chip{ min-height:44px; padding:0 18px; border-radius:12px; font:inherit; font-size:15px; font-weight:700; cursor:pointer; border:0;
  display:inline-flex; align-items:center; justify-content:center; gap:6px; transition:transform .12s ease, filter .15s, background .15s, box-shadow .15s; }
.tb-btn{ background:var(--navy); color:var(--cream); }
.tb-gold{ background:linear-gradient(135deg,#f0cf8a,#d9a957); color:var(--navy); box-shadow:0 6px 16px rgba(185,138,62,.28); }
.tb-ghost{ background:var(--beige); color:var(--brown); }
.tb-chip{ min-height:36px; padding:0 14px; border:1px solid #d8dde6; background:#fff; color:var(--ink); border-radius:999px; font-size:14px; font-weight:600; }
.tb-chip[aria-pressed="true"]{ background:var(--navy); border-color:var(--navy); color:var(--cream); }
:is(.tb-btn,.tb-gold,.tb-ghost,.tb-chip):hover:not(:disabled){ filter:brightness(1.06); }
:is(.tb-btn,.tb-gold,.tb-ghost,.tb-chip):active:not(:disabled){ transform:scale(.97); }
:is(.tb-btn,.tb-gold,.tb-ghost):disabled{ opacity:.45; cursor:not-allowed; box-shadow:none; }
.tb :focus-visible{ outline:2px solid var(--gold); outline-offset:2px; }
.tb-note{ font-size:13px; color:var(--muted); }
.tb-warn,.tb-err,.tb-ok{ padding:10px 12px; border-radius:12px; font-size:14px; margin-top:12px; animation:tbFade .25s ease; }
.tb-warn{ background:var(--mid-bg); color:#6b4500; }
.tb-err{ background:var(--bad-bg); color:#7f1d1d; }
.tb-ok{ background:var(--ok-bg); color:#14532d; }
.tb-mono{ white-space:pre-wrap; font:13px/1.5 ui-monospace,Menlo,monospace; background:#0f1730; color:#d9e2ff; padding:12px; border-radius:12px; max-height:320px; overflow-y:auto; }
.tb-item{ padding:10px 0; border-bottom:1px solid #eef1f6; }
.tb-item:last-child{ border-bottom:0; }
.tb-panel{ margin-top:14px; padding:14px; border:1px solid #ecdcb8; background:linear-gradient(180deg,#fffaf0,#fff); border-radius:14px; animation:tbUp .35s ease both; }
.tb-day{ margin-top:14px; }
.tb-day > strong{ display:inline-block; font-size:12px; padding:3px 10px; border-radius:999px; background:var(--blue-bg); color:var(--blue); }
.tb-time{ display:inline-block; min-width:52px; font-weight:700; color:var(--gold-ink); }
.tb-chat{ display:grid; gap:10px; max-height:440px; overflow-y:auto; padding:4px 2px; margin:12px 0; }
.tb-bubble{ padding:10px 14px; border-radius:18px; white-space:pre-wrap; font-size:15px; max-width:85%; animation:tbPop .25s ease both; }
.tb-me{ justify-self:end; background:var(--navy); color:#fff; border-end-end-radius:6px; transform-origin:right bottom; }
.tb-milo{ justify-self:start; background:#eef1f6; border-end-start-radius:6px; transform-origin:left bottom; }
.tb-typing{ display:inline-flex; gap:4px; padding:14px 16px; }
.tb-typing i{ width:7px; height:7px; border-radius:50%; background:var(--faint); animation:tbDot 1s infinite ease-in-out; }
.tb-typing i:nth-child(2){ animation-delay:.15s; } .tb-typing i:nth-child(3){ animation-delay:.3s; }
.tb-composer{ display:flex; gap:8px; align-items:flex-end; padding:8px; border:1px solid var(--line); border-radius:18px; background:#fff; box-shadow:0 4px 18px rgba(21,38,61,.06); }
.tb-composer textarea{ flex:1; border:0; resize:none; font:inherit; font-size:16px; padding:8px; min-height:40px; max-height:140px; background:transparent; color:var(--ink); }
.tb-composer textarea:focus{ outline:none; }
.tb-round{ width:44px; height:44px; min-height:44px; padding:0; border-radius:14px; flex:0 0 44px; font-size:18px; }
.tb-round.tb-rec{ background:#d93a3a; color:#fff; animation:tbRec 1.2s ease-in-out infinite; }
@keyframes tbRec{ 50%{ box-shadow:0 0 0 8px rgba(217,58,58,.18); } }
@media (prefers-reduced-motion: reduce){ .tb-round.tb-rec{ animation:none; } }
.tb-badge{ flex:0 0 74px; text-align:center; font-size:12px; font-weight:700; border-radius:999px; padding:3px 0; }
.tb-badge.done{ color:var(--ok); background:var(--ok-bg); } .tb-badge.partial{ color:var(--mid); background:var(--mid-bg); } .tb-badge.missing{ color:var(--bad); background:var(--bad-bg); }
.tb-big{ font-size:22px; font-weight:650; }
.tb-file{ position:relative; overflow:hidden; }
.tb-file input{ position:absolute; inset:0; opacity:0; cursor:pointer; }
.tb-milo-card{ border-radius:20px; padding:16px; background:linear-gradient(160deg,#fff6e6 0%,#fffdf8 60%,#fff 100%); border:1px solid #f0e2c6; }
.tb-milo-top{ display:flex; align-items:center; gap:10px; }
.tb-avatar{ width:42px; height:42px; border-radius:14px; display:grid; place-items:center; font-size:24px; background:#fde9c8; animation:tbBob 3.2s ease-in-out infinite; }
.tb-milo-card .tb-greet{ font-size:17px; font-weight:600; margin:12px 0; }
.tb-eye{ background:#fff; border:1px solid #f0e6d4; border-radius:14px; padding:12px; }
.tb-eye small{ color:var(--faint); font-size:11px; }
.tb-eye strong{ display:block; font-size:16px; margin:2px 0; }
.tb-notice{ box-sizing:border-box; display:block; width:100%; background:#fffaf0; border:1px solid #ecdcb8; border-radius:14px; padding:12px; margin-bottom:10px; animation:tbUp .35s ease both; }
.tb-update{ display:grid; gap:4px; margin-top:8px; padding:10px 12px; border:1px dashed #d9c08f; border-radius:12px; background:#fff; font-size:14px; }
.tb-update-row{ display:flex; flex-wrap:wrap; gap:6px; align-items:baseline; }
.tb-update-row > span:first-child{ color:var(--faint); min-width:84px; }
.tb-update-row s{ color:var(--faint); }
.tb-bellwrap{ position:relative; }
.tb-head > div:first-child + .tb-bellwrap{ margin-inline-start:auto; }
.tb-bell{ all:unset; cursor:pointer; position:relative; width:40px; height:40px; display:grid; place-items:center; border-radius:12px; font-size:18px; }
.tb-bell:hover{ background:#eef1f6; }
.tb-bell:focus-visible{ outline:2px solid var(--gold); }
.tb-bell-n{ position:absolute; top:2px; inset-inline-end:2px; min-width:18px; height:18px; padding:0 4px; box-sizing:border-box; border-radius:999px; background:#d93a3a; color:#fff; font-size:11px; font-weight:800; display:grid; place-items:center; }
.tb-bellpop{ position:absolute; top:46px; inset-inline-end:0; z-index:30; width:min(340px, calc(100vw - 32px)); max-height:70vh; overflow:auto; background:#fff; border:1px solid var(--line); border-radius:16px; padding:12px; box-shadow:0 16px 40px rgba(21,38,61,.16); display:grid; gap:4px; }
.tb-bellitem{ all:unset; cursor:pointer; display:flex; gap:10px; padding:8px; border-radius:10px; font-size:14px; }
.tb-bellitem:hover, .tb-bellitem:focus-visible{ background:#f6f3ec; }
.tb-bellitem small{ display:block; color:var(--faint); font-size:11px; }
.tb-bellitem strong{ display:block; font-weight:600; }
.tb-bellitem em{ font-style:normal; font-size:11px; font-weight:800; color:var(--gold-ink); }
.tb-notice small{ color:var(--faint); font-size:11px; }
.tb-notice strong{ display:block; font-size:16px; margin:2px 0; }
.tb-link{ background:none; border:0; padding:0; color:var(--gold-ink); font:inherit; font-size:13px; font-weight:600; cursor:pointer; }
.tb-talk{ width:100%; margin-top:12px; justify-content:space-between; }
.tb-quick{ display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.tb-quick button{ text-align:start; background:#fff; border:1px solid var(--line); border-radius:16px; padding:12px; cursor:pointer; font:inherit; color:var(--ink); transition:transform .15s, box-shadow .15s; }
.tb-quick button:hover{ transform:translateY(-2px); box-shadow:0 8px 20px rgba(21,38,61,.08); }
.tb-quick small{ display:block; color:var(--faint); font-size:12px; }
.tb-quick strong{ font-size:14px; }
.tb-hero{ position:relative; height:280px; border-radius:20px; overflow:hidden; color:#fff; isolation:isolate; }
.tb-hero img{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; z-index:-2; animation:tbKen 22s ease-in-out infinite alternate; }
.tb-hero::before{ content:""; position:absolute; inset:0; z-index:-1; background:linear-gradient(90deg,rgba(16,32,57,.86),rgba(16,32,57,.22)); }
.tb-hero-in{ position:absolute; inset:auto 18px 18px 18px; }
.tb-hero small{ font-size:11px; font-weight:800; letter-spacing:.08em; text-transform:uppercase; opacity:.9; }
.tb-hero h2{ color:#fff; margin:6px 0 4px; font-size:32px; font-weight:800; line-height:1.15; text-shadow:0 2px 12px rgba(0,0,0,.25); }
.tb-hero p{ margin:0; font-size:13px; opacity:.9; }
.tb-hero .tb-chip{ position:absolute; inset-inline-end:14px; top:14px; background:rgba(255,255,255,.92); border:0; color:var(--ink); }
.tb-tiles{ display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:10px; }
.tb-tile{ text-align:start; background:#fff; border:1px solid var(--line); border-radius:16px; padding:14px 12px; cursor:pointer; font:inherit; color:var(--ink); transition:transform .15s, box-shadow .15s, border-color .15s; }
.tb-tile:hover{ transform:translateY(-2px); box-shadow:0 10px 24px rgba(21,38,61,.08); border-color:#e8dcc3; }
.tb-tile span{ display:block; font-size:18px; color:var(--gold-ink); }
.tb-tile strong{ display:block; font-size:14px; margin-top:6px; }
.tb-tile small{ color:var(--faint); font-size:12px; }
.tb-tabs{ position:sticky; bottom:0; z-index:20; display:grid; grid-template-columns:repeat(5,1fr); padding:6px 8px calc(8px + env(safe-area-inset-bottom)); background:rgba(255,255,255,.95); backdrop-filter:blur(10px); border-top:1px solid var(--line); }
.tb-tabs button{ position:relative; background:none; border:0; padding:6px 0 4px; display:grid; justify-items:center; gap:2px; font:inherit; font-size:11px; color:var(--faint); cursor:pointer; min-height:48px; }
.tb-tabs button span{ font-size:18px; line-height:1; transition:transform .2s; }
.tb-tabs button[aria-current="true"]{ color:var(--gold-ink); font-weight:700; }
.tb-tabs button[aria-current="true"] span{ transform:translateY(-2px) scale(1.12); }
.tb-tabs button[aria-current="true"]::before{ content:""; position:absolute; top:-6px; width:26px; height:3px; border-radius:3px; background:var(--gold); animation:tbGrow .25s ease; }
.tb-live{ display:inline-block; width:9px; height:9px; border-radius:50%; background:#e0483e; margin-inline-end:6px; animation:tbPulse 1.4s infinite; vertical-align:middle; }
.tb-sample{ min-height:36px; padding:0 12px; border:0; border-radius:999px; background:linear-gradient(135deg,#f0cf8a,#d9a957); color:var(--navy); font:inherit; font-size:13px; font-weight:700; cursor:pointer; white-space:nowrap; }
.tb-sample:active{ transform:scale(.97); }
@media (max-width: 520px){ .tb-sample span{ display:none; } }
.tb-lang{ min-height:36px; max-width:150px; padding:0 10px; border:1px solid var(--line); border-radius:999px; background:#fff; color:var(--ink); font:inherit; font-size:13px; font-weight:600; cursor:pointer; }
.tb-pill + .tb-lang{ margin-inline-start:0; }
.tb-head > .tb-lang:only-of-type{ margin-inline-start:auto; }
.tb-arrow{ display:inline-block; }
.tb[dir="rtl"] .tb-arrow{ transform:scaleX(-1); }
.tb[dir="rtl"] .tb-hero::before{ background:linear-gradient(270deg,rgba(16,32,57,.86),rgba(16,32,57,.22)); }
.tb[dir="rtl"] .tb-me{ transform-origin:left bottom; }
.tb[dir="rtl"] .tb-milo{ transform-origin:right bottom; }
.tb:is([lang="ar"],[lang="fa"],[lang="ur"]) *{ letter-spacing:normal !important; }
.tb-dot{ width:10px; height:10px; border-radius:50%; background:var(--faint); flex:0 0 10px; }
.tb-dot.ready{ background:#22a35a; box-shadow:0 0 0 3px rgba(34,163,90,.18); }
.tb-dot.connecting{ background:var(--gold); animation:tbPulse 1.4s infinite; }
.tb-dot.error{ background:#e0483e; }
.tb-callbar{ display:flex; flex-wrap:wrap; gap:12px; align-items:center; justify-content:space-between; margin-top:12px; padding:14px; border-radius:16px; background:var(--navy); color:#fff; animation:tbUp .3s ease both; }
.tb-callbar .tb-note{ color:rgba(255,255,255,.75); }
.tb-callbar.ring{ background:linear-gradient(135deg,#1d7a42,#17263d); animation:tbUp .3s ease both, tbRing 1.2s ease-in-out infinite; }
.tb-callbar .tb-ghost{ background:rgba(255,255,255,.12); color:#fff; }
.tb-callbar .tb-ghost[aria-pressed="true"]{ background:var(--cream); color:var(--navy); }
.tb-ok-btn{ background:#22a35a; color:#fff; }
.tb-bad-btn{ background:#d6453b; color:#fff; }
.tb-places{ display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:8px; }
.tb-place{ border:1px solid var(--line); border-radius:14px; background:#fff; padding:10px 12px; display:grid; gap:6px; transition:border-color .15s, box-shadow .15s; }
.tb-place[aria-current="true"]{ border-color:var(--gold); box-shadow:0 0 0 3px rgba(185,138,62,.15); }
.tb-place-main{ all:unset; cursor:pointer; display:grid; gap:2px; text-align:start; }
.tb-place-main small{ color:var(--faint); font-size:12px; }
.tb-prep-title{ margin:2px 0 4px; font-size:18px; color:var(--ink); }
.tb-facts{ display:grid; grid-template-columns:max-content 1fr; gap:4px 14px; margin:0; font-size:14px; }
.tb-facts dt{ color:var(--muted); }
.tb-facts dd{ margin:0; font-weight:600; }
.tb-tips{ margin:8px 0 0; padding-inline-start:18px; font-size:14px; color:var(--muted); }
.tb-keypad{ display:grid; grid-template-columns:repeat(3,72px); gap:8px; justify-content:center; margin-top:12px; }
.tb-key{ height:56px; border-radius:16px; border:1px solid var(--line); background:#fff; font:inherit; font-size:22px; font-weight:600; color:var(--ink); cursor:pointer; transition:transform .1s, background .15s; }
.tb-key:active{ transform:scale(.94); background:var(--beige); }
@keyframes tbRing{ 0%,100%{ transform:translateX(0); } 20%{ transform:translateX(-2px); } 40%{ transform:translateX(2px); } }
.tb-wizard{ margin-top:12px; }
.tb-choices{ display:grid; grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); gap:8px; }
.tb-choice{ all:unset; box-sizing:border-box; cursor:pointer; display:grid; gap:2px; padding:12px; border:1px solid var(--line); border-radius:14px; background:#fff; transition:border-color .15s, box-shadow .15s, transform .12s; }
.tb-choice:hover{ transform:translateY(-1px); box-shadow:0 6px 16px rgba(21,38,61,.06); }
.tb-choice[aria-pressed="true"]{ border-color:var(--gold); box-shadow:0 0 0 3px rgba(185,138,62,.18); background:#fffaf0; }
.tb-choice:disabled{ cursor:not-allowed; opacity:.45; filter:grayscale(1); transform:none; box-shadow:none; }
.tb-choice:focus-visible{ outline:2px solid var(--gold); outline-offset:2px; }
.tb-choice small{ color:var(--faint); font-size:12px; }
.tb-gb{ font-size:22px; font-weight:800; }
.tb-price{ margin-top:4px; font-weight:700; color:var(--ink); font-size:14px; }
.tb-price.inc{ color:var(--ok); }
.tb-pay strong{ font-size:14px; }
.tb-numbers{ display:grid; grid-template-columns:repeat(auto-fill,minmax(180px,1fr)); gap:8px; margin-top:8px; }
.tb-number{ all:unset; box-sizing:border-box; cursor:pointer; text-align:center; padding:12px; border:1px solid var(--line); border-radius:12px; background:#fff; font-weight:700; font-size:16px; letter-spacing:.02em; }
.tb-number[aria-checked="true"]{ border-color:var(--gold); background:#fffaf0; box-shadow:0 0 0 3px rgba(185,138,62,.18); }
.tb-number:focus-visible{ outline:2px solid var(--gold); }
.tb-receipt{ padding:12px 14px; border:1px dashed #d8dde6; border-radius:12px; background:#fff; }
.tb-receipt .tb-total{ border-top:1px solid var(--line); padding-top:8px; margin-top:4px; font-weight:800; color:var(--ink); }
.tb-numcard{ display:flex; gap:12px; justify-content:space-between; align-items:center; margin-top:12px; padding:14px; border-radius:16px; border:1px solid #ecdcb8; background:linear-gradient(135deg,#fffaf0,#fff); animation:tbUp .35s ease both; }
.tb-numcard small{ color:var(--faint); font-size:12px; }
.tb-numbig{ display:block; font-size:22px; letter-spacing:.02em; }
.tb-callcard{ display:grid; gap:8px; margin-top:12px; padding:14px; border:1px solid var(--line); border-radius:16px; background:#fff; animation:tbUp .35s ease both; }
.tb-callcard-top{ display:flex; gap:12px; justify-content:space-between; align-items:flex-start; }
.tb-callcard-top .tb-badge{ flex:0 0 auto; padding:3px 10px; white-space:nowrap; }
.tb-callcard :is(.tb-ok,.tb-err,.tb-warn){ margin:0; }
.tb-change{ display:flex; gap:10px; align-items:flex-start; }
.tb-route{ display:grid; gap:8px; margin-top:8px; padding:12px; border:1px solid var(--line); border-radius:14px; background:#fff; }
.tb-route-modes{ display:grid; gap:6px; }
.tb-placecards{ display:grid; grid-template-columns:repeat(auto-fill, minmax(200px, 1fr)); gap:8px; margin-top:6px; }
.tb-place{ display:grid; gap:2px; padding:12px 14px; border:1px solid var(--line); border-radius:16px; background:#fff; }
.tb-place strong{ font-size:16px; }
.tb-maplink{ display:inline-flex; align-items:center; gap:6px; text-decoration:none; }
.tb-route-mode{ display:flex; align-items:center; justify-content:space-between; gap:8px; padding:6px 8px; border-radius:10px; border:1px solid transparent; }
.tb-route-mode[aria-current="true"]{ border-color:var(--gold); background:#fffaf0; }
.tb-route-pick{ all:unset; cursor:pointer; display:flex; gap:10px; align-items:center; min-width:0; }
.tb-route-pick:disabled{ cursor:default; opacity:.65; }
.tb-route-pick:focus-visible{ outline:2px solid var(--gold); border-radius:8px; }
.tb-route-pick > span:first-child{ font-size:20px; }
.tb-route-pick small{ display:block; color:var(--faint); font-size:12px; }
.tb-route-lines{ margin:0; padding-inline-start:18px; font-size:13px; display:grid; gap:4px; }
.tb-route-line{ display:inline-block; padding:0 6px; border-inline-start:4px solid var(--navy); background:#eef1f6; border-radius:4px; font-weight:700; }
.tb-route-map{ width:100%; border-radius:12px; border:1px solid var(--line); }
.tb-driver{ all:unset; box-sizing:border-box; cursor:pointer; display:grid; gap:2px; padding:12px; border-radius:12px; background:#15263d; color:#fff; }
.tb-driver small{ opacity:.75; font-size:12px; }
.tb-driver strong{ font-size:18px; }
.tb-driver.big strong{ font-size:34px; line-height:1.2; }
.tb-driver.big span{ font-size:20px; }
.tb-driver:focus-visible{ outline:2px solid var(--gold); outline-offset:2px; }
.tb-translated{ margin-top:4px; padding-top:4px; border-top:1px dashed currentColor; font-size:13px; opacity:.8; }
.tb-change > span{ font-size:18px; line-height:1.3; }
.tb-esim-plan{ display:flex; gap:12px; align-items:center; justify-content:space-between; padding:14px; border-radius:16px; background:linear-gradient(135deg,#17263d,#24386b); color:#fff; margin-top:12px; }
.tb-esim-plan .tb-note{ color:rgba(255,255,255,.75); }
.tb-steps{ display:flex; gap:6px; list-style:none; padding:0; margin:14px 0 4px; counter-reset:s; }
.tb-steps li{ flex:1; text-align:center; font-size:12px; color:var(--faint); padding-top:8px; border-top:3px solid var(--line); counter-increment:s; }
.tb-steps li::before{ content:counter(s) ". "; }
.tb-steps li.done{ color:var(--ok); border-top-color:#22a35a; }
.tb-steps li[aria-current="step"]{ color:var(--gold-ink); font-weight:700; border-top-color:var(--gold); }
.tb-howto{ margin:10px 0; padding-inline-start:20px; font-size:14px; display:grid; gap:4px; }
.tb-esim-codes{ display:flex; flex-wrap:wrap; gap:16px; align-items:center; margin:10px 0; }
.tb-esim-codes img{ border-radius:12px; background:#fff; padding:6px; border:1px solid var(--line); }
.tb-meter{ height:12px; border-radius:999px; background:#e7ebf2; overflow:hidden; margin:10px 0 6px; }
.tb-meter span{ display:block; height:100%; background:linear-gradient(90deg,#2bb673,#22a35a); border-radius:999px; transition:width .6s ease; }
.tb-meter span.mid{ background:linear-gradient(90deg,#e8b04b,#d9a957); }
.tb-meter span.bad{ background:linear-gradient(90deg,#ee6a5f,#d6453b); }
@supports (animation-timeline: view()){
  .tb-reveal{ animation:tbRise linear both; animation-timeline:view(); animation-range:entry 0% entry 40%; }
}
@keyframes tbRise{ from{ opacity:0; transform:translateY(24px) scale(.98); } to{ opacity:1; transform:none; } }
@keyframes tbUp{ from{ opacity:0; transform:translateY(10px); } to{ opacity:1; transform:none; } }
@keyframes tbFade{ from{ opacity:0; } to{ opacity:1; } }
@keyframes tbPop{ from{ opacity:0; transform:scale(.92); } to{ opacity:1; transform:none; } }
@keyframes tbDot{ 0%,80%,100%{ transform:translateY(0); opacity:.4; } 40%{ transform:translateY(-5px); opacity:1; } }
@keyframes tbBob{ 0%,100%{ transform:translateY(0) rotate(0); } 50%{ transform:translateY(-3px) rotate(-4deg); } }
@keyframes tbKen{ from{ transform:scale(1); } to{ transform:scale(1.12) translate(-2%,-2%); } }
@keyframes tbPulse{ 0%{ box-shadow:0 0 0 0 rgba(224,72,62,.6); } 100%{ box-shadow:0 0 0 10px rgba(224,72,62,0); } }
@keyframes tbGrow{ from{ width:0; } to{ width:26px; } }
@media (max-width: 760px){
  .tb-layout{ grid-template-columns:minmax(0,1fr); padding:0; gap:0; }
  .tb-side{ display:none; }
  .tb-phone{ border-radius:0; box-shadow:none; min-height:100vh; }
  .tb-head{ border-radius:0; padding:14px 16px 10px; }
  .tb-body{ padding:4px 12px 100px; }
  .tb-hero h2{ font-size:23px; }
}
@media (prefers-reduced-motion: reduce){
  .tb *, .tb *::before{ animation:none !important; transition:none !important; }
}
`;

const EMPTY = {
  trip: { travelerId: '', tripId: '', travelerName: '', destination: '', area: '', startDate: '', endDate: '', hotelName: '', hotelPhone: '', roomNumber: '', nationality: '' },
  prefs: { party: '', pace: '', budget: '', interests: '', food: '', mobility: '' },
  itinerary: null,
  itineraryHistory: [],
  bookings: [],
  calls: [],
  esim: { status: 'none' },
  number: null,
  notices: {},
  seenAt: '',
  credits: null,
  phoneLog: [],
  textLog: [],
};

/**
 * A 7-day Kyoto & Osaka family trip where today is day 3, so every section has something to work with.
 * phone: if given, every sample place uses it (so AI calls and the dialer ring you); otherwise each place
 * gets a 555-01xx number, which is reserved for fiction and never connects.
 */
function sampleTrip({ name = 'Allen', phone = '' } = {}) {
  const day = (offset) => new Date(Date.now() + offset * 86400000).toLocaleDateString('en-CA');
  const stop = (time, title, place, durationMin, transport, note = '') => ({ time, title, place, durationMin, transport, note });
  const tel = (n) => phone || `+1 805 555 01${String(n).padStart(2, '0')}`;
  const traveler = name.trim() || 'Allen';
  const booking = (type, bookingName, start, end, address, n, confirmation, party = '') => ({
    type, name: bookingName, start, end, timezone: 'Asia/Tokyo', address, phone: n == null ? '' : tel(n), confirmation, party, source: 'sample',
  });
  return {
    trip: {
      travelerId: 'traveler_sample_allen',
      tripId: 'trip_sample_kyoto_family',
      travelerName: traveler,
      destination: 'Kyoto & Osaka, Japan',
      area: 'Gion',
      startDate: day(-2),
      endDate: day(4),
      hotelName: 'Hotel Gion Sakura (sample)',
      hotelPhone: tel(0),
      roomNumber: '412',
      nationality: '',
    },
    prefs: {
      party: `3 adults — ${traveler} and parents`,
      pace: 'relaxed, rest break every afternoon',
      budget: 'about $150 per person per day',
      interests: 'temples, local food, tea & cafés, gardens',
      food: 'no raw fish; dad is allergic to shellfish',
      mobility: 'parents prefer less walking and few stairs',
    },
    bookings: [
      booking('flight', 'Hong Kong → Osaka KIX (sample)', `${day(-2)}T09:10`, `${day(-2)}T14:05`, '', null, 'SAMPLE-FLT-77QX', '3'),
      booking('hotel', 'Hotel Gion Sakura (sample)', `${day(-2)}T15:00`, `${day(4)}T11:00`, 'Higashiyama-ku, Kyoto', 0, 'SAMPLE-HTL-1042', '3'),
      booking('restaurant', 'Gion Kappa (sample)', `${day(0)}T19:00`, null, 'Gion, Kyoto', 1, 'SAMPLE-RST-203', '3'),
      booking('restaurant', 'Kikunoi Honten kaiseki lunch (sample)', `${day(1)}T12:00`, null, 'Higashiyama, Kyoto', 2, 'SAMPLE-RST-318', '3'),
      booking('activity', 'Tea ceremony at Camellia (sample)', `${day(2)}T15:00`, null, 'Higashiyama, Kyoto', 3, 'SAMPLE-ACT-552', '3'),
      booking('restaurant', 'Mizuno okonomiyaki, Dotonbori (sample)', `${day(3)}T18:30`, null, 'Dotonbori, Osaka', 4, 'SAMPLE-RST-640', '3'),
      booking('flight', 'Osaka KIX → Hong Kong (sample)', `${day(4)}T16:20`, `${day(4)}T19:30`, '', null, 'SAMPLE-FLT-81RT', '3'),
    ],
    itinerary: {
      days: [
        { date: day(-2), items: [stop('14:05', 'Land at Kansai Airport', 'KIX', 60, 'Haruka express to Kyoto'), stop('16:00', 'Check in and rest', 'Hotel Gion Sakura', 120, 'Taxi from Kyoto Station')] },
        { date: day(-1), items: [stop('10:00', 'Fushimi Inari (lower gates only)', 'Fushimi Inari Taisha', 90, 'JR Nara line', 'Skip the summit climb'), stop('15:00', 'Nishiki Market tasting', 'Nishiki Market', 90, 'Taxi')] },
        { date: day(0), items: [stop('09:30', 'Kiyomizu-dera', 'Kiyomizu-dera', 120, 'Taxi up the hill, walk down'), stop('14:30', 'An easy afternoon in Gion', 'Gion', 60, 'About 15 minutes on foot', 'Tea break'), stop('17:00', 'Hotel rest', 'Hotel Gion Sakura', 90, 'Walk'), stop('19:00', 'Family dinner', 'Gion Kappa', 90, 'Walk', 'Booked, table for 3')] },
        { date: day(1), items: [stop('10:00', 'Arashiyama bamboo grove', 'Arashiyama', 90, 'JR Sagano line'), stop('12:00', 'Kaiseki lunch', 'Kikunoi Honten', 90, 'Taxi', 'Booked, no shellfish for dad')] },
        { date: day(2), items: [stop('10:30', 'Kinkaku-ji (Golden Pavilion)', 'Kinkaku-ji', 75, 'Taxi'), stop('15:00', 'Tea ceremony', 'Camellia', 60, 'Taxi', 'Booked')] },
        { date: day(3), items: [stop('10:00', 'Osaka Castle', 'Osaka Castle', 120, 'JR Special Rapid to Osaka'), stop('18:30', 'Okonomiyaki dinner', 'Mizuno', 75, 'Subway to Namba', 'Booked')] },
        { date: day(4), items: [stop('09:00', 'Check out', 'Hotel Gion Sakura', 30, ''), stop('12:00', 'Head to Kansai Airport', 'KIX', 90, 'Haruka express')] },
      ],
      toVerify: ['Opening hours', 'Tea ceremony availability'],
    },
    itineraryHistory: [],
    calls: [],
  };
}

const newId = (prefix = '') =>
  prefix + (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

/** Every trip gets a traveler id and trip id so conversations can be tied back to them. */
/** A blank state with new traveler and trip ids, so the old call and text feed no longer shows. */
export const freshState = () => withIds(EMPTY);

function withIds(state) {
  const trip = { ...EMPTY.trip, ...state.trip };
  if (!trip.travelerId) trip.travelerId = newId('traveler_');
  if (!trip.tripId) trip.tripId = newId('trip_');
  return { ...state, trip };
}

function loadState() {
  let state;
  try {
    state = withIds({ ...EMPTY, ...JSON.parse(localStorage.getItem(STATE_KEY) || '{}') });
    if (state.esim?.number && !state.number) {
      const { number, ...esim } = state.esim;
      state = { ...state, number, esim };
    }
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    state = state || withIds(EMPTY);
  }
  return state;
}

/** Traveler + trip metadata sent with every AI request and stored with the conversation. */
function metaOf(state) {
  const tr = state.trip;
  return {
    traveler: { id: tr.travelerId, name: tr.travelerName },
    trip: { id: tr.tripId, destination: tr.destination, startDate: tr.startDate, endDate: tr.endDate, area: tr.area, hotelName: tr.hotelName },
    sample: tr.tripId.startsWith('trip_sample_'),
    locale: locale(),
  };
}

/** The UI language as the AI should read it ("Japanese", "Chinese (Traditional)"…). */
const uiLanguage = () => languageOf(locale()).name;

/** Shared by every section: trip context for the AI, conversation metadata, and adding a booking. */
const Bench = createContext({ ctx: {}, meta: {}, addBooking: () => {}, state: {}, updateWith: () => {}, feed: {} });

const api = (path) => `${getTravelApiBaseUrl()}/api/p3/${path}`;

/** The travel API needs a signed-in, verified account: a lost session goes to log-in, an unverified email to verification. */
function signInAgainIf(res, data) {
  if (res.status === 401) {
    setSessionToken('');
    window.location.hash = '#/p3-login';
  } else if (res.status === 403 && data?.code === 'EMAIL_NOT_VERIFIED') {
    window.location.hash = '#/p3-verify';
  }
}

async function requestJson(url, { method = 'GET', body } = {}) {
  const res = await fetch(url, {
    method,
    headers: { ...(body === undefined ? {} : { 'Content-Type': 'application/json' }), ...authHeaders() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  signInAgainIf(res, data);
  if (!res.ok || !data?.success) {
    const err = new Error(data?.message || t('common.requestFailed', { status: String(res.status) }));
    err.status = res.status;
    throw err;
  }
  return data;
}
const postJson = (url, body) => requestJson(url, { method: 'POST', body });

/** An image from the travel API, which needs the log-in header a plain <img src> can't send. */
function SignedInImage({ src, ...rest }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let live = true;
    let made = '';
    fetch(src, { headers: authHeaders() })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (b && live) setUrl((made = URL.createObjectURL(b)));
      })
      .catch(() => {});
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [src]);
  // eslint-disable-next-line jsx-a11y/alt-text
  return url ? <img src={url} {...rest} /> : null;
}

/** Shrinks a photo to ≤1600px JPEG so uploads stay small. Rejects non-images and files over 8 MB. */
function readImage(file) {
  return new Promise((resolve, reject) => {
    if (!/^image\/(png|jpe?g|webp|gif)$/.test(file.type)) return reject(new Error(t('image.badType')));
    if (file.size > 8 * 1024 * 1024) return reject(new Error(t('image.tooBig')));
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(img.src);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => reject(new Error(t('image.unreadable')));
    img.src = URL.createObjectURL(file);
  });
}

function speak(text, lang) {
  if (!window.speechSynthesis || !text) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  if (lang) u.lang = lang;
  window.speechSynthesis.speak(u);
}

let jumpOverride = null;
/** The P3 app shows sections as screens and sheets, so it handles "go to section" itself. */
export const setJumpOverride = (fn) => { jumpOverride = fn; };

function jumpTo(id) {
  if (jumpOverride?.(id)) return;
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/* ---------- small building blocks ---------- */

/** Forward arrow that flips in right-to-left languages. */
const Arrow = () => <span className="tb-arrow" aria-hidden="true">→</span>;

/** Phone numbers, ids and codes stay left-to-right inside RTL text. */
const Ltr = ({ children }) => <bdi dir="ltr">{children}</bdi>;

function Field({ id, label, value, onChange, ...rest }) {
  return (
    <div className="tb-f">
      <label className="tb-label" htmlFor={id}>{label}</label>
      <input id={id} className="tb-input" value={value || ''} onChange={(e) => onChange(e.target.value)} {...rest} />
    </div>
  );
}

function Select({ id, label, value, onChange, options, ...rest }) {
  return (
    <div className="tb-f">
      <label className="tb-label" htmlFor={id}>{label}</label>
      <select id={id} className="tb-input" value={value} onChange={(e) => onChange(e.target.value)} {...rest}>
        {options.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
      </select>
    </div>
  );
}

function FileButton({ label, onFile, className = 'tb-ghost', capture }) {
  return (
    <label className={`${className} tb-file`}>
      {label}
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        capture={capture}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onFile(f);
        }}
      />
    </label>
  );
}

function Section({ id, kicker, desc, children }) {
  return (
    <section id={id} data-spy className="tb-card tb-reveal" aria-labelledby={`${id}-h`}>
      <header>
        <span className="tb-kicker">{kicker}</span>
        <h3 id={`${id}-h`}>{t(`title.${id}`)}</h3>
        {desc && <p>{desc}</p>}
      </header>
      {children}
    </section>
  );
}

const join = (items) => items.join(' · ');
const label = (prefix, value) => t(`${prefix}.${value}`) === `${prefix}.${value}` ? value : t(`${prefix}.${value}`);

/** Runs one companion tool with the shared trip context; the AI answers in the UI language. */
function useTool(ctx, bench) {
  const context = useContext(Bench);
  const { meta } = bench || context;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [noCredits, setNoCredits] = useState(false);
  const run = async (tool, input = {}, extra = {}) => {
    setBusy(true);
    setError('');
    setNoCredits(false);
    try {
      const { result } = await postJson(api('companion'), { tool, input, context: ctx, uiLanguage: uiLanguage(), conversationId: newId('tool_'), meta, ...extra });
      return result;
    } catch (e) {
      setError(e.message);
      setNoCredits(e.status === 402);
      return null;
    } finally {
      setBusy(false);
    }
  };
  return { run, busy, error, setError, noCredits };
}

/* ---------- AI call review: shared by AI Calling, chat and hotel ---------- */

// Call languages offered up front (all have a voice); the review dropdown lists every supported one.
const CALL_LANGS = ['en', 'zh', 'ja', 'ko', 'th', 'vi', 'id', 'ms', 'fil', 'hi', 'ar', 'tr', 'ru', 'es', 'fr', 'de', 'it', 'pt'];

const OUTCOME_CLASS = {
  confirmed: 'tb-ok', declined: 'tb-err', needs_approval: 'tb-warn', uncertain: 'tb-warn', voicemail: 'tb-warn',
  no_answer: 'tb-warn', busy: 'tb-warn', failed: 'tb-err', in_progress: 'tb-warn',
};

// Fired when a call is placed or its result read, so Milo's calls refreshes.
const CALLS_CHANGED = 'p3:calls-changed';

// Mirrors CALL_OPTION_DEFAULTS in backend/travel/p3CallPlanner.js
const CALL_OPTION_DEFAULTS = { declineFees: true, alternatives: true, askExtra: false, confirmBack: false };

function CallReview({ instructions: initial = '', travelerName = '', onPlaced, autoPlan = false }) {
  const { ctx, meta, addBooking } = useContext(Bench);
  const conversationId = useRef(newId('call_'));
  const [outcome, setOutcome] = useState(null);
  const [saved, setSaved] = useState(false);
  const [instructions, setInstructions] = useState(initial);
  const [plan, setPlan] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [call, setCall] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [from, setFrom] = useState(DEFAULT_FROM_NUMBER);
  const [useTen, setUseTen] = useState(true);
  const { billing: callCosts } = useBilling(requestJson, api);
  const [callLang, setCallLang] = useState('en');
  const [options, setOptions] = useState(CALL_OPTION_DEFAULTS);
  const wsRef = useRef(null);

  useEffect(() => () => wsRef.current?.close(), []);

  const makePlan = async (language = '', text = instructions) => {
    setBusy('plan');
    setError('');
    try {
      const data = await postJson(api('plan-call'), { instructions: text, travelerName, language, uiLanguage: uiLanguage(), context: ctx, conversationId: conversationId.current, meta });
      setPlan(data.plan);
      setLanguages(data.languages || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    if (autoPlan && initial) makePlan(callLang, initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watchTranscript = (to, callSid) => {
    wsRef.current?.close();
    try {
      const ws = new WebSocket(getLiveTranscriptWsUrl(null));
      ws.onmessage = (ev) => {
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (msg.type !== 'update' || (msg.sessionId !== callSid && msg.callerNumber !== to)) return;
        if (typeof msg.transcript === 'string') setTranscript(msg.transcript);
        if (msg.active === false) setCall((c) => (c ? { ...c, ended: true } : c));
      };
      wsRef.current = ws;
    } catch {
      /* transcript is best-effort; the call still runs */
    }
  };

  const placeCall = async () => {
    setBusy('call');
    setError('');
    setTranscript('');
    try {
      setOutcome(null);
      setSaved(false);
      const data = await postJson(api('call'), { plan, from, useTen, options, uiLanguage: uiLanguage(), conversationId: conversationId.current, meta });
      const entry = { callSid: data.callSid, to: data.to || plan.to, business: plan.businessName, language: plan.language, goal: plan.goal, at: new Date().toISOString() };
      setCall(entry);
      onPlaced?.(entry);
      window.dispatchEvent(new Event(CALLS_CHANGED));
      watchTranscript(entry.to, data.callSid);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const checkOutcome = async () => {
    if (!call?.callSid) return;
    setBusy('outcome');
    try {
      const data = await postJson(api('call-outcome'), { callSid: call.callSid, transcript, plan, uiLanguage: uiLanguage(), context: ctx, conversationId: conversationId.current, meta });
      setOutcome(data.outcome);
      window.dispatchEvent(new Event(CALLS_CHANGED));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  // Read the result once the live feed says the call ended.
  useEffect(() => {
    if (call?.ended && !outcome) checkOutcome();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.ended]);

  const saveBooking = () => {
    const b = outcome.booking;
    addBooking({ type: b.type || 'other', name: b.name, start: b.start || '', end: null, timezone: '', address: '', phone: plan?.to || '', confirmation: b.confirmation || outcome.confirmationNumber || '', source: 'ai-call', confirmed: outcome.outcome === 'confirmed' });
    setSaved(true);
  };

  const edit = (k) => (v) => setPlan({ ...plan, [k]: v });
  const langOptions = [
    ...(languages.some((l) => l.code === plan?.language) || !plan ? [] : [[plan.language, t('call.noVoiceOption', { language: languageName(plan.language) })]]),
    ...languages.map((l) => [l.code, languageName(l.code)]),
  ];

  return (
    <div>
      <label className="tb-label" htmlFor="call-instr">{t('call.instructions')}</label>
      <textarea
        id="call-instr"
        className="tb-input"
        rows={3}
        placeholder={t('call.instructionsPh')}
        value={instructions}
        onChange={(e) => setInstructions(e.target.value)}
      />
      <div className="tb-row" style={{ marginTop: 4 }}>
        <Select id="call-lang-pick" label={t('call.langPick')} value={callLang} onChange={setCallLang} options={[['', t('call.langAuto')], ...CALL_LANGS.map((c) => [c, languageName(c)])]} />
        <Field id="call-from" label={t('call.callerId')} value={from} onChange={setFrom} dir="ltr" inputMode="tel" />
        <button className="tb-btn" disabled={!instructions.trim() || !!busy} onClick={() => makePlan(callLang)}>
          {busy === 'plan' ? t('call.planning') : plan ? t('call.replan') : t('call.plan')}
        </button>
      </div>
      <label className="tb-note" style={{ display: 'inline-flex', gap: 6, marginTop: 10 }}>
        <input type="checkbox" checked={useTen} onChange={(e) => setUseTen(e.target.checked)} /> {t('call.ten')}
      </label>
      {useTen && <p className="tb-note" style={{ margin: '4px 0 0' }}>🌐 {t('call.langSwitch')}</p>}

      {plan && (
        <div className="tb-panel">
          <div className="tb-row">
            <Field id="call-to" label={t('call.to')} value={plan.to} onChange={edit('to')} placeholder="+81…" dir="ltr" inputMode="tel" />
            <Field id="call-biz" label={t('call.business')} value={plan.businessName} onChange={edit('businessName')} />
          </div>
          {plan.toSource && plan.to === plan.toSource.phone && (
            <div className="tb-warn">
              <strong>{t('call.found', { name: plan.toSource.name || plan.businessName })}</strong>
              {plan.toSource.address && <> · {plan.toSource.address}</>} ·{' '}
              <a href={plan.toSource.source} target="_blank" rel="noopener noreferrer">{t('call.source')}</a>
              <div>{t('call.checkNumber')}</div>
            </div>
          )}
          {plan.lookupFailed && !plan.to && <div className="tb-warn">{t('call.notFound')}</div>}
          <div className="tb-row">
            <Select id="call-lang" label={t('call.language')} value={plan.language} disabled={!!busy} onChange={(v) => { setCallLang(v); makePlan(v); }} options={langOptions} />
          </div>
          {plan.goal && <p className="tb-note">{t('call.goal', { text: plan.goal })}</p>}
          <label className="tb-label" htmlFor="call-first">{t('call.opening')}</label>
          <textarea id="call-first" className="tb-input" rows={2} lang={plan.language} dir="auto" value={plan.firstMessage} onChange={(e) => edit('firstMessage')(e.target.value)} />
          <div className="tb-label">{t('call.opts')}</div>
          <div className="tb-row" role="group" aria-label={t('call.opts')}>
            {Object.keys(CALL_OPTION_DEFAULTS).map((k) => (
              <button key={k} className="tb-chip" aria-pressed={options[k]} onClick={() => setOptions((o) => ({ ...o, [k]: !o[k] }))}>
                {options[k] ? '✓ ' : ''}{t(`call.opt.${k}`)}
              </button>
            ))}
          </div>
          <details style={{ marginTop: 12 }}>
            <summary className="tb-label" style={{ cursor: 'pointer' }}>{t('call.script')}</summary>
            <textarea id="call-script" aria-label={t('call.script')} className="tb-input" rows={4} lang={plan.language} dir="auto" value={plan.script} onChange={(e) => edit('script')(e.target.value)} />
          </details>
          {plan.missing?.length > 0 && <div className="tb-warn">{t('call.missing', { items: join(plan.missing) })}</div>}
          {!plan.supported && <div className="tb-err">{t('call.noVoice', { language: languageName(plan.language) })}</div>}
          {callCosts && <p className="tb-note">💳 {t('pay.callMax', { n: formatNumber(callCosts.callHold), per: formatNumber(callCosts.costs.callPerMinute) })}</p>}
          <div className="tb-row" style={{ marginTop: 14 }}>
            <button className="tb-gold" disabled={!plan.to || !plan.supported || !plan.script || !!busy} onClick={placeCall}>
              {busy === 'call' ? t('call.dialing') : `📞 ${t('call.go')}`}
            </button>
          </div>
        </div>
      )}

      {error && <div className="tb-err" role="alert">{error}</div>}

      {call && (
        <div style={{ marginTop: 14 }}>
          <div className={call.ended ? 'tb-ok' : 'tb-warn'} role="status">
            {!call.ended && <span className="tb-live" aria-hidden="true" />}
            <strong>{call.ended ? t('call.ended') : t('call.inProgress')}</strong> · <Ltr>{call.to}</Ltr> · {languageName(call.language)} · <Ltr>SID {call.callSid}</Ltr>
          </div>
          <div className="tb-mono" style={{ marginTop: 10 }} dir="auto">{transcript || t('call.waiting')}</div>
          {!outcome && (
            <button className="tb-ghost" style={{ marginTop: 10 }} onClick={checkOutcome} disabled={busy === 'outcome'}>
              {busy === 'outcome' ? t('call.reading') : t('call.check')}
            </button>
          )}
          {outcome && (
            <div className="tb-panel">
              <span className="tb-kicker">{t('call.result')}</span>
              <div className={OUTCOME_CLASS[outcome.outcome] || 'tb-warn'} style={{ marginTop: 6 }}>
                <strong>{label('outcome', outcome.outcome)}</strong> — {outcome.summary}
              </div>
              {Object.values(outcome.details || {}).some(Boolean) && (
                <p className="tb-note">{join(Object.entries(outcome.details).filter(([, v]) => v).map(([k, v]) => `${label('detail', k)}: ${v}`))}</p>
              )}
              {outcome.confirmationNumber && <p className="tb-note">{t('call.confirmation', { code: outcome.confirmationNumber })}</p>}
              {outcome.nextStep && <p className="tb-note">{t('call.next', { text: outcome.nextStep })}</p>}
              <div className="tb-row" style={{ marginTop: 8 }}>
                {outcome.booking && (
                  <button className="tb-gold" onClick={saveBooking} disabled={saved}>{saved ? t('call.saved') : t('call.save')}</button>
                )}
                <button className="tb-ghost" onClick={checkOutcome} disabled={busy === 'outcome'}>{t('call.recheck')}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- sections ---------- */

/** Trip dates: an end date before the start is refused; moving the start past the end moves the end with it. */
export function tripDates(trip, key, value) {
  if (key === 'endDate' && value && trip.startDate && value < trip.startDate) return { error: 'trip.endBeforeStart' };
  if (key === 'startDate' && value && trip.endDate && value > trip.endDate) return { trip: { ...trip, startDate: value, endDate: value } };
  return { trip: { ...trip, [key]: value } };
}

function TripSection({ state, update, sampleOpen, setSampleOpen }) {
  const [dateError, setDateError] = useState('');
  const setTrip = (k) => (v) => update({ trip: { ...state.trip, [k]: v } });
  const setDate = (k) => (v) => {
    const r = tripDates(state.trip, k, v);
    setDateError(r.error ? t(r.error) : '');
    if (r.trip) update({ trip: r.trip });
  };
  const setPref = (k) => (v) => update({ prefs: { ...state.prefs, [k]: v } });
  return (
    <Section id="trip" kicker={t('kicker.setup')} desc={t('trip.desc')}>
      {sampleOpen && <SampleFiller state={state} update={update} onDone={() => setSampleOpen(false)} />}
      <div className="tb-row">
        <Field id="t-name" label={t('trip.name')} value={state.trip.travelerName} onChange={setTrip('travelerName')} />
        <Field id="t-dest" label={t('trip.destination')} value={state.trip.destination} onChange={setTrip('destination')} placeholder={t('trip.destinationPh')} />
      </div>
      <div className="tb-row">
        <Field id="t-start" label={t('trip.start')} type="date" value={state.trip.startDate} onChange={setDate('startDate')} />
        <Field id="t-end" label={t('trip.end')} type="date" value={state.trip.endDate} onChange={setDate('endDate')} min={state.trip.startDate || undefined} aria-invalid={dateError ? true : undefined} aria-describedby={dateError ? 't-date-err' : undefined} />
      </div>
      {dateError && <div className="tb-err" id="t-date-err" role="alert">{dateError}</div>}
      <div className="tb-row">
        <Field id="t-area" label={t('trip.area')} value={state.trip.area} onChange={setTrip('area')} placeholder={t('trip.areaPh')} />
        <Field id="t-nat" label={t('trip.nationality')} value={state.trip.nationality} onChange={setTrip('nationality')} />
      </div>
      <div className="tb-row">
        <Field id="t-hotel" label={t('trip.hotel')} value={state.trip.hotelName} onChange={setTrip('hotelName')} />
        <Field id="t-hphone" label={t('trip.hotelPhone')} value={state.trip.hotelPhone} onChange={setTrip('hotelPhone')} placeholder="+81 3 …" dir="ltr" inputMode="tel" />
        <Field id="t-room" label={t('trip.room')} value={state.trip.roomNumber} onChange={setTrip('roomNumber')} placeholder="412" dir="ltr" />
      </div>
      <div className="tb-sub">{t('trip.prefs')}</div>
      <div className="tb-row">
        <Field id="p-party" label={t('trip.party')} value={state.prefs.party} onChange={setPref('party')} placeholder={t('trip.partyPh')} />
        <Field id="p-pace" label={t('trip.pace')} value={state.prefs.pace} onChange={setPref('pace')} placeholder={t('trip.pacePh')} />
      </div>
      <div className="tb-row">
        <Field id="p-budget" label={t('trip.budget')} value={state.prefs.budget} onChange={setPref('budget')} placeholder={t('trip.budgetPh')} />
        <Field id="p-int" label={t('trip.interests')} value={state.prefs.interests} onChange={setPref('interests')} placeholder={t('trip.interestsPh')} />
      </div>
      <div className="tb-row">
        <Field id="p-food" label={t('trip.food')} value={state.prefs.food} onChange={setPref('food')} placeholder={t('trip.foodPh')} />
        <Field id="p-mob" label={t('trip.mobility')} value={state.prefs.mobility} onChange={setPref('mobility')} />
      </div>
      <div style={{ marginTop: 16 }}>
        <div className="tb-row">
          <button className="tb-btn" onClick={() => { setSampleOpen(true); jumpTo('trip'); }}>✨ {t('trip.loadSample')}</button>
          <button className="tb-ghost" onClick={() => window.confirm(t('trip.confirmReset')) && update(withIds(EMPTY))}>{t('trip.reset')}</button>
        </div>
      </div>
    </Section>
  );
}

// Trip fields the AI sometimes names directly ("hotelPhone") → the form label the traveler sees.
const FIELD_LABELS = {
  travelerName: 'trip.name', destination: 'trip.destination', startDate: 'trip.start', endDate: 'trip.end', area: 'trip.area',
  nationality: 'trip.nationality', hotelName: 'trip.hotel', hotelPhone: 'trip.hotelPhone', roomNumber: 'trip.room', party: 'trip.party', pace: 'trip.pace',
  budget: 'trip.budget', interests: 'trip.interests', food: 'trip.food', mobility: 'trip.mobility',
};
const missingLabel = (item) => (FIELD_LABELS[String(item).trim()] ? t(FIELD_LABELS[String(item).trim()]) : item);

// Browser speech-to-text (Chrome, Edge, Safari). Where it's missing the mic button is hidden.
// ponytail: browser recognizer; switch to server transcription if Firefox or accuracy matters.
const SpeechRecognition = typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null;
const SPEECH_LANG = { 'zh-Hans': 'zh-CN', 'zh-Hant': 'zh-TW', fil: 'fil-PH' };

/** Dictation in the UI language: onText(text, false) while speaking, onText(text, true) once at the end. */
function useDictation(onText) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');
  const rec = useRef(null);
  const latest = useRef(onText);
  latest.current = onText;
  useEffect(() => () => rec.current?.abort(), []);

  const start = () => {
    const r = new SpeechRecognition();
    r.lang = SPEECH_LANG[locale()] || locale();
    r.interimResults = true;
    let heard = '';
    r.onresult = (e) => {
      heard = Array.from(e.results).map((x) => x[0].transcript).join('');
      latest.current(heard, false);
    };
    r.onerror = (e) => {
      if (e.error !== 'no-speech' && e.error !== 'aborted') setError(e.error === 'not-allowed' || e.error === 'service-not-allowed' ? t('chat.micBlocked') : t('chat.micError'));
    };
    r.onend = () => {
      rec.current = null;
      setListening(false);
      if (heard.trim()) latest.current(heard.trim(), true);
    };
    rec.current = r;
    setError('');
    setListening(true);
    try {
      r.start();
    } catch {
      setListening(false);
      setError(t('chat.micError'));
    }
  };

  return { supported: Boolean(SpeechRecognition), listening, error, toggle: () => (listening ? rec.current?.stop() : start()) };
}

/** Up to three flights Milo found with web search: estimates with sources, saved as options. */
function FlightOptions({ data }) {
  const { addBooking } = useContext(Bench);
  const [saved, setSaved] = useState({});

  const save = (o, i) => {
    addBooking({
      type: 'flight', name: join([o.airline, o.flightNumbers, `${o.fromAirport} → ${o.toAirport}`].filter(Boolean)),
      start: data.date && o.depart ? `${data.date}T${o.depart}` : '', end: data.date && o.arrive ? `${data.date}T${o.arrive}` : null,
      timezone: '', address: '', phone: '', confirmation: '', party: data.adults ? String(data.adults) : '', source: 'milo-flights',
    });
    setSaved({ ...saved, [i]: true });
  };

  return (
    <div className="tb-panel" style={{ marginTop: 0 }}>
      <strong>✈ {t('flights.title')}{data.from && data.to ? ` · ${data.from} → ${data.to}` : ''}{data.date ? ` · ${formatDate(data.date)}` : ''}</strong>
      {data.options.length === 0 && <p className="tb-note">{t('flights.none')}</p>}
      {data.options.map((o, i) => (
        <article key={i} className="tb-callcard">
          <div className="tb-callcard-top">
            <div style={{ minWidth: 0 }}>
              <strong>{o.airline}{o.flightNumbers ? <> · <Ltr>{o.flightNumbers}</Ltr></> : ''}</strong>
              <div className="tb-note">
                <Ltr>{join([o.depart && o.arrive ? `${o.fromAirport} ${o.depart} → ${o.toAirport} ${o.arrive}` : `${o.fromAirport} → ${o.toAirport}`, o.duration].filter(Boolean))}</Ltr>
                {o.stops != null && ` · ${o.stops === 0 ? t('flights.nonstop') : t('flights.stopsN', { n: o.stops })}`}
              </div>
            </div>
            <span className="tb-badge done">{t(`flights.pick.${o.pick}`)}</span>
          </div>
          {o.price && <div><strong>{o.price}</strong> <span className="tb-note">· {t('flights.estimate')}</span></div>}
          {o.why && <div className="tb-note">{o.why}</div>}
          <div className="tb-row" style={{ alignItems: 'center' }}>
            <button className="tb-ghost" onClick={() => save(o, i)} disabled={saved[i]}>{saved[i] ? t('call.saved') : t('call.save')}</button>
            <a className="tb-link" href={o.source} target="_blank" rel="noopener noreferrer">{t('flights.source')}</a>
          </div>
        </article>
      ))}
      {data.note && <p className="tb-note">{data.note}</p>}
      {data.searchUrl && <a className="tb-link" href={data.searchUrl} target="_blank" rel="noopener noreferrer">{t('flights.google')} <Arrow /></a>}
    </div>
  );
}

/** Confirmed: a reference number, or a call that ended "confirmed". A flight option from a search never is. */
export const isConfirmedBooking = (b) => b.source !== 'milo-flights' && Boolean(b.confirmation || b.confirmed === true);

const SEARCH_STALE_MS = 3 * 60000;

const mapsQuery = (...parts) => encodeURIComponent(parts.filter(Boolean).join(', '));

/** A place as a card with a map and directions link (free Google Maps URLs, no key). */
/** The trip's hotel as a map search ("Hotel name, City"), or '' when none is set. */
const hotelOrigin = (trip = {}) => withCity(String(trip.hotelName || '').replace(/\s*\(sample\)/i, '').trim(), trip.destination);

function PlaceCard({ name, note, where, source }) {
  const { state } = useContext(Bench);
  const hotel = hotelOrigin(state?.trip);
  // Directions start at the hotel when the trip has one; otherwise Google Maps uses the phone's location.
  const directions = `https://www.google.com/maps/dir/?api=1${hotel ? `&origin=${mapsQuery(hotel)}` : ''}&destination=${mapsQuery(name, where)}`;
  return (
    <article className="tb-place">
      <strong>{name}</strong>
      {note && <div className="tb-note">{note}</div>}
      {where && <div className="tb-note" dir="auto">📍 {where}</div>}
      <div className="tb-row" style={{ alignItems: 'center', marginTop: 4 }}>
        <a className="tb-chip tb-maplink" href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery(name, where)}`} target="_blank" rel="noopener noreferrer">🗺 {t('place.map')}</a>
        <a className="tb-chip tb-maplink" href={directions} target="_blank" rel="noopener noreferrer" title={hotel ? t('place.fromHotel', { hotel }) : undefined}>🧭 {hotel ? t('place.fromHotelShort') : t('search.directions')}</a>
        {source && <a className="tb-link" href={source} target="_blank" rel="noopener noreferrer">{t('flights.source')}</a>}
      </div>
    </article>
  );
}

/** Places Milo suggested in a chat reply. */
function PlaceCards({ places }) {
  return (
    <div className="tb-placecards">
      {places.slice(0, 5).map((p, i) => <PlaceCard key={i} name={p.name} note={p.note} where={p.area} />)}
    </div>
  );
}

/** A general web answer: headline, summary and sourced items. */
function WebAnswer({ data }) {
  return (
    <div className="tb-panel" style={{ marginTop: 0 }}>
      <strong>🔎 {data.headline || t('search.label')}</strong>
      {data.summary && <p className="tb-note" style={{ margin: '4px 0' }}>{data.summary}</p>}
      {!data.summary && !data.items.length && <p className="tb-note" style={{ margin: '4px 0' }}>{t('search.nothing')}</p>}
      {data.items.length > 0 && (
        <div className="tb-placecards">
          {data.items.map((it, i) => <PlaceCard key={i} name={it.title} note={it.detail} where={it.address} source={it.source} />)}
        </div>
      )}
      {data.note && <p className="tb-note">{data.note}</p>}
    </div>
  );
}

/** A background search's state from the feed: still running, failed, or its result. */
function SearchResult({ c }) {
  const job = c?.search;
  if (!job || job.status === 'running') {
    const stale = job && Date.now() - new Date(job.startedAt) > SEARCH_STALE_MS;
    return <div className="tb-note">{stale ? t('search.stale') : <><span className="tb-live" aria-hidden="true" />{t('search.running')}</>}</div>;
  }
  if (job.status === 'failed') return <div className="tb-err" style={{ margin: 0 }}>{t('search.failed', { error: job.error || '' })}</div>;
  return job.kind === 'flights' ? <FlightOptions data={job.result} /> : <WebAnswer data={job.result} />;
}

/** A search started from chat: follows its job on the feed (refreshed every 10 s). */
function SearchJob({ id }) {
  const { feed } = useContext(Bench);
  const inFeed = feed.items?.find((c) => c.conversationId === id);
  const [own, setOwn] = useState(null);
  const done = (inFeed || own)?.search?.status && (inFeed || own).search.status !== 'running';

  // Normally the feed carries the search; if it doesn't (e.g. saved under another traveler), fetch it by id.
  useEffect(() => {
    if (inFeed || done) return undefined;
    const load = () => fetch(`${api('conversations')}?conversationId=${encodeURIComponent(id)}&limit=1`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        const c = d?.conversations?.find((x) => x.conversationId === id);
        if (c) setOwn(c);
      })
      .catch(() => {});
    const timer = setInterval(load, 5000);
    load();
    return () => clearInterval(timer);
  }, [id, inFeed, done]);

  return <SearchResult c={inFeed || own} />;
}

/**
 * Companion chat state: sends a message with the trip context and turns Milo's reply into actions
 * (call review, background search, route card, or "open this tool"). onOpen(toolId) is called when
 * Milo opens a tool.
 */
/** bench: pass the shared state when the hook is used above the Bench provider (the P3 app does). */
const threadKey = (accountId) => `p3_chat_thread_${accountId}`;
const rememberThread = (accountId, id) => {
  try {
    localStorage.setItem(threadKey(accountId), id);
  } catch {
    /* storage is a convenience only */
  }
};

/** A saved chat thread (server conversation) → the messages the chat shows. Call cards wait for a tap instead of re-planning. */
export function threadMessages(conversation) {
  return (conversation?.messages || []).map((m) => {
    const at = new Date(m.at).getTime() || 0;
    if (m.role !== 'assistant') return { role: 'user', content: m.content, at };
    const { call, ...cards } = m.meta || {};
    return { role: 'assistant', content: m.content, at, ...cards, ...(call ? { call: { ...call, restored: true } } : {}) };
  });
}

export function useMiloChat(ctx, { onOpen, bench } = {}) {
  const context = useContext(Bench);
  const { meta, feed } = bench || context;
  const { run, busy, error, noCredits } = useTool(ctx, bench || context);
  const threadId = useRef(newId('chat_'));
  const [messages, setMessages] = useState([]);
  const accountId = meta?.traveler?.id?.startsWith('acct_') ? meta.traveler.id : '';

  // Signed in: show the chat that was open (else the latest one), so a reload or another device picks up where it left off.
  useEffect(() => {
    if (!accountId) return undefined;
    let live = true;
    let saved = '';
    try {
      saved = localStorage.getItem(threadKey(accountId)) || '';
    } catch {
      /* storage is a convenience only */
    }
    const query = saved ? `conversationId=${encodeURIComponent(saved)}&limit=1` : 'channel=chat&limit=1';
    requestJson(`${api('conversations')}?${query}`)
      .then(({ conversations }) => {
        const thread = conversations?.find((c) => c.channel === 'chat');
        if (!live || !thread) return;
        setMessages((now) => {
          if (now.length) return now;
          threadId.current = thread.conversationId;
          return threadMessages(thread);
        });
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [accountId]);
  const [text, setText] = useState('');
  const [callSids, setCallSids] = useState([]);
  const mic = useDictation((heard, done) => (done ? send(heard) : setText(heard)));

  const send = async (spoken) => {
    const message = (typeof spoken === 'string' ? spoken : text).trim();
    if (!message || busy) return;
    setText('');
    // Milo also sees what happened in the background (texts, call results, searches) so follow-ups work.
    const history = [
      ...chatEvents(feed?.items || [], { messages, callSids }).map((e) => ({ at: e.at, role: 'assistant', content: eventText(e.c, feed?.items) })),
      ...messages.map((m) => ({ at: m.at || Infinity, role: m.role, content: m.content })),
    ].sort((a, b) => a.at - b.at).map(({ role, content }) => ({ role, content }));
    setMessages((m) => [...m, { role: 'user', content: message, at: Date.now() }]);
    if (accountId) rememberThread(accountId, threadId.current);
    const result = await run('chat', { message }, { history, conversationId: threadId.current });
    if (!result) return;
    const ask = result.search?.request;
    const searchId = ask ? result.searchId || newId('search_') : null;
    const open = OPEN_TOOLS.includes(result.open) ? result.open : null;
    setMessages((m) => [...m, { role: 'assistant', content: result.reply || '', call: result.call, searchId, route: result.route, open, places: Array.isArray(result.places) ? result.places.filter((p) => p?.name) : [], missing: result.missing, at: Date.now() }]);
    if (open) onOpen?.(open);
    if (!searchId) return;
    try {
      await postJson(api('search'), { kind: result.search.kind === 'flights' ? 'flights' : 'web', request: ask, context: ctx, uiLanguage: uiLanguage(), conversationId: searchId, meta });
      window.dispatchEvent(new Event(CALLS_CHANGED));
    } catch (e) {
      setMessages((m) => m.map((x) => (x.searchId === searchId ? { ...x, searchError: e.message } : x)));
    }
  };
  const reset = () => {
    setMessages([]);
    setCallSids([]);
    threadId.current = newId('chat_');
    if (accountId) rememberThread(accountId, threadId.current);
  };
  // Calls placed from this chat already show their result inline, so the timeline skips them.
  const addCall = (sid) => sid && setCallSids((c) => [...c, sid]);
  return { messages, text, setText, send, busy, error, noCredits, mic, reset, callSids, addCall };
}

// Tools Milo can open from chat (section ids; the P3 app maps them to screens and sheets).
export const OPEN_TOOLS = ['trip', 'plan', 'bookings', 'esim', 'number', 'translate', 'hotel', 'local', 'ready', 'food', 'dialer', 'results'];

/** The chat messages with whatever Milo set in motion for each reply. */
const EVENT_WINDOW_MS = 3 * 86400000;

/**
 * What happened in the background, for the chat timeline: texts to Milo (with their results), messages
 * from businesses, finished calls, and searches not already shown in this chat. Oldest first.
 */
export function chatEvents(items = [], chat) {
  const inChat = new Set(chat.messages.map((m) => m.searchId).filter(Boolean));
  const calls = new Set(chat.callSids || []);
  const since = Date.now() - EVENT_WINDOW_MS;
  return items
    .filter((c) => {
      if (c.channel === 'search') return c.search.source !== 'sms' && !inChat.has(c.conversationId);
      if (c.channel === 'call') return Boolean(c.call?.outcome?.analyzedAt) && !calls.has(c.call.callSid);
      return c.channel === 'sms';
    })
    .map((c) => ({ c, at: new Date(c.channel === 'sms' ? c.sms.receivedAt || c.createdAt : c.channel === 'call' ? c.call.outcome.analyzedAt : c.search.startedAt || c.createdAt).getTime() }))
    .filter((e) => e.at >= since)
    .sort((a, b) => a.at - b.at);
}

/** A background event as plain text for Milo's chat history (English, like the rest of the prompt). */
function eventText(c, items = []) {
  if (c.channel === 'sms') {
    const n = c.sms.notification;
    if (n.sender === 'traveler') {
      const r = items.find((x) => x.conversationId === n.taskId)?.search?.result;
      return `[The traveler texted Milo: "${c.sms.body}"]${r ? ` Result: ${JSON.stringify(r).slice(0, 1500)}` : ''}`;
    }
    return `[Text from ${n.business || c.sms.from}: "${c.sms.body}"] ${n.summary || ''}`;
  }
  if (c.channel === 'search') return `[Milo searched: "${c.search.request}"] Result: ${JSON.stringify(c.search.result || {}).slice(0, 1500)}`;
  const o = c.call.outcome;
  return `[Milo called ${c.call.businessName || c.call.to} about: ${c.call.goal || ''}] Result: ${o.outcome}. ${o.summary || ''}`;
}

/** One background event as it appears in the chat. */
function ChatEvent({ c }) {
  if (c.channel === 'sms') return <SmsNotice c={c} />;
  if (c.channel === 'search') {
    return (
      <>
        <div className="tb-bubble tb-me" dir="auto">{c.search.request}</div>
        <SearchResult c={c} />
      </>
    );
  }
  const l = feedLine(c);
  const o = c.call.outcome;
  return (
    <article className="tb-callcard">
      <div className="tb-callcard-top">
        <div style={{ minWidth: 0 }}>
          <strong>📞 {l.who}</strong>
          <div className="tb-note">{formatDateTime(l.at)}</div>
        </div>
        <span className={`tb-badge ${o.outcome === 'confirmed' ? 'done' : 'partial'}`}>{label('outcome', o.outcome)}</span>
      </div>
      <div className={`tb-change ${OUTCOME_CLASS[o.outcome] || 'tb-warn'}`}>
        <span aria-hidden="true">{l.icon}</span>
        <div><strong>{l.title}</strong>{o.change?.headline && o.summary && <div>{o.summary}</div>}</div>
      </div>
      <button className="tb-link" style={{ justifySelf: 'start' }} onClick={() => jumpTo('results')}>{t('notify.openAll')}</button>
    </article>
  );
}

export function ChatThread({ chat, state, listRef, className = 'tb-chat', events = [] }) {
  const { messages, busy } = chat;
  // Chat messages and background events in time order (a message with no time stays after the events).
  const timeline = [
    ...events.map((e) => ({ at: e.at, event: e.c })),
    ...messages.map((m, i) => ({ at: m.at || Infinity, m, i })),
  ].sort((a, b) => a.at - b.at);
  return (
    <div className={className} ref={listRef} aria-live="polite">
      {timeline.map(({ m, i, event }) => (event ? <ChatEvent key={event.conversationId} c={event} /> : (
        <React.Fragment key={i}>
          <div className={`tb-bubble ${m.role === 'user' ? 'tb-me' : 'tb-milo'}`} dir="auto">{m.content}</div>
          {m.missing?.length > 0 && (
            <div className="tb-warn" style={{ marginTop: 0 }}>
              {t('chat.needs', { items: join(m.missing.map(missingLabel)) })}{' '}
              <button className="tb-link" onClick={() => jumpTo('trip')}>{t('today.setUp')} <Arrow /></button>
            </div>
          )}
          {m.open && <button className="tb-ghost" style={{ justifySelf: 'start' }} onClick={() => jumpTo(m.open)}>{t(`title.${m.open}`)} <Arrow /></button>}
          {m.places?.length > 0 && <PlaceCards places={m.places} />}
          {m.route?.to && <RouteCard from={m.route.from || ''} to={m.route.to} />}
          {m.searchId && (m.searchError ? <div className="tb-err" role="alert">{m.searchError}</div> : <SearchJob id={m.searchId} />)}
          {m.call?.instructions && (
            <div className="tb-panel" style={{ marginTop: 0 }}>
              <strong>{t('chat.callReview')}</strong>
              <CallReview instructions={m.call.instructions} travelerName={state.trip.travelerName} autoPlan={!m.call.restored} onPlaced={(e) => chat.addCall?.(e.callSid)} />
            </div>
          )}
        </React.Fragment>
      )))}
      {busy && <div className="tb-bubble tb-milo tb-typing" aria-label={t('chat.thinking')}><i /><i /><i /></div>}
    </div>
  );
}

/** Message box with mic and send. */
export function ChatComposer({ chat, id = 'chat-in' }) {
  const { text, setText, send, busy, mic } = chat;
  return (
    <>
      <div className="tb-composer">
        <textarea
          id={id}
          aria-label={t('chat.message')}
          placeholder={mic.listening ? t('chat.listening') : t('chat.placeholder')}
          rows={1}
          dir="auto"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
            }
          }}
        />
        {mic.supported && (
          <button className={`tb-ghost tb-round${mic.listening ? ' tb-rec' : ''}`} aria-label={mic.listening ? t('chat.listening') : t('chat.voice')} aria-pressed={mic.listening} onClick={mic.toggle} disabled={busy}>
            {mic.listening ? '■' : '🎙'}
          </button>
        )}
        <button className="tb-btn tb-round" aria-label={t('chat.send')} onClick={send} disabled={!text.trim() || busy}>✦</button>
      </div>
      {mic.error && <div className="tb-err" role="alert">{mic.error}</div>}
    </>
  );
}

function ChatSection({ ctx, state }) {
  const { feed } = useContext(Bench);
  const chat = useMiloChat(ctx);
  const listRef = useRef(null);
  const events = chatEvents(feed.items || [], chat);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [chat.messages, chat.busy, events.length]);

  return (
    <Section id="chat" kicker={t('kicker.companion')} desc={t('chat.desc')}>
      {chat.messages.length === 0 && (
        <div className="tb-row">
          {[t('chat.example1'), t('chat.example2'), t('chat.example3')].map((q) => (
            <button key={q} className="tb-chip" onClick={() => chat.setText(q)}>{q}</button>
          ))}
        </div>
      )}
      <ChatThread chat={chat} state={state} listRef={listRef} events={events} />
      <ChatComposer chat={chat} />
      {chat.messages.length > 0 && (
        <button className="tb-link" style={{ marginTop: 8 }} onClick={chat.reset}>{t('chat.new')}</button>
      )}
      {chat.error && <div className="tb-err" role="alert">{chat.error}</div>}
    </Section>
  );
}

/** "Place, City" unless the place already names the city. */
const withCity = (place, destination) => {
  const city = String(destination || '').split(/[,&]/)[0].trim();
  return place && city && !place.toLowerCase().includes(city.toLowerCase()) ? `${place}, ${city}` : place || '';
};

function DayList({ days, routes = false }) {
  const { state } = useContext(Bench);
  const [open, setOpen] = useState('');
  return days.map((d) => (
    <div key={d.date} className="tb-day">
      <strong>{formatDate(d.date)}</strong>
      {(d.items || []).map((it, i) => {
        const id = `${d.date}-${i}`;
        const to = withCity(it.place || it.title, state.trip?.destination);
        const from = withCity(d.items[i - 1]?.place || String(state.trip?.hotelName || '').replace(/\s*\(sample\)/, ''), state.trip?.destination);
        return (
          <div key={i} className="tb-item" style={it.cancelled ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}>
            <span className="tb-time"><Ltr>{it.time}</Ltr></span> {it.title}
            <div className="tb-note">{join([it.place, it.durationMin && t('plan.minutes', { n: it.durationMin }), it.transport, it.note].filter(Boolean))}</div>
            {routes && to && !it.cancelled && (
              <button className="tb-link" style={{ marginTop: 4 }} aria-expanded={open === id} onClick={() => setOpen(open === id ? '' : id)}>
                🧭 {open === id ? t('route.hide') : t('route.getThere')}
              </button>
            )}
            {open === id && <RouteCard from={from} to={to} date={d.date} time={it.time} />}
          </div>
        );
      })}
    </div>
  ));
}

const ROUTE_MODES = [['TRANSIT', '🚆', 'route.transit'], ['WALK', '🚶', 'route.walk'], ['DRIVE', '🚕', 'route.drive']];

/**
 * Walk / taxi / transit options between two places (Google Maps), a static map of the chosen one,
 * a local-language card to show a driver, and free links out. Unknown routes say so and link to Google Maps.
 */
function RouteCard({ from, to, date, time }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('');
  const [driver, setDriver] = useState(false);

  useEffect(() => {
    postJson(api('route'), { from, to, date, time, uiLanguage: locale() })
      .then((r) => {
        setData(r);
        setMode((r.routes.find((x) => x.mode === 'TRANSIT' && x.available) || r.routes.find((x) => x.available))?.mode || '');
      })
      .catch((e) => setError(e.message));
  }, [from, to, date, time]);

  if (error) return <div className="tb-err" role="alert">{error}</div>;
  if (!data) return <div className="tb-note"><span className="tb-live" aria-hidden="true" />{t('route.loading')}</div>;

  // No Google key on the server: just hand off to Google Maps (free, no key).
  if (data.configured === false) {
    return (
      <div className="tb-route">
        <strong>🧭 {t('route.title', { place: to })}</strong>
        {from && <div className="tb-note">{t('route.from', { place: from })}</div>}
        <div className="tb-row">
          {ROUTE_MODES.map(([m, icon, label]) => (
            <a key={m} className="tb-chip tb-maplink" href={data.links.google[m]} target="_blank" rel="noopener noreferrer">{icon} {t(label)}</a>
          ))}
        </div>
        <div className="tb-note">{t('route.opensMaps')}</div>
      </div>
    );
  }

  const dest = data.destination;
  const chosen = data.routes.find((r) => r.mode === mode);
  const ll = (p) => (p?.location ? `${p.location.latitude},${p.location.longitude}` : '');
  const mapSrc = chosen && data.origin && dest ? `${api('route-map')}?${new URLSearchParams({ poly: chosen.polyline || '', a: ll(data.origin), b: ll(dest) })}` : '';

  return (
    <div className="tb-route">
      <strong>🧭 {t('route.title', { place: dest?.name || to })}</strong>
      {from && <div className="tb-note">{t('route.from', { place: data.origin?.name || from })}{data.arriveBy && time ? ` · ${t('route.arriveBy', { time })}` : ''}</div>}
      {data.configured === false && <p className="tb-note">{t('route.notSetUp')}</p>}
      {data.configured && data.found === false && <p className="tb-note">{t('route.notFound')}</p>}

      <div className="tb-route-modes">
        {ROUTE_MODES.map(([m, icon, label]) => {
          const r = data.routes.find((x) => x.mode === m) || { available: false };
          return (
            <div key={m} className="tb-route-mode" aria-current={mode === m ? 'true' : undefined}>
              <button className="tb-route-pick" disabled={!r.available} onClick={() => setMode(m)}>
                <span aria-hidden="true">{icon}</span>
                <span>
                  <strong>{t(label)}</strong>
                  <small>{r.available ? join([r.duration || t('plan.minutes', { n: r.durationMin }), r.distance, r.fare?.text].filter(Boolean)) : t('route.unknown')}</small>
                </span>
              </button>
              <a className="tb-link" href={data.links.google[m]} target="_blank" rel="noopener noreferrer">{t('route.openMaps')}</a>
            </div>
          );
        })}
      </div>

      {chosen?.lines?.length > 0 && (
        <ol className="tb-route-lines">
          {chosen.lines.map((l, i) => (
            <li key={i}>
              <span className="tb-route-line" style={l.color ? { borderColor: l.color } : undefined}>{l.line || l.vehicle}</span>
              {' '}<Ltr>{join([l.depart && `${l.depart} ${l.from}`, l.arrive && `${l.arrive} ${l.to}`].filter(Boolean)).replace(' · ', ' → ')}</Ltr>
            </li>
          ))}
        </ol>
      )}

      {mapSrc && <SignedInImage className="tb-route-map" src={mapSrc} alt={t('route.title', { place: dest?.name || to })} loading="lazy" onError={(e) => { e.currentTarget.hidden = true; }} />}

      {dest && (
        <button className={`tb-driver${driver ? ' big' : ''}`} onClick={() => setDriver(!driver)} aria-pressed={driver}>
          <small>{driver ? t('route.driverHint') : `🚕 ${t('route.driver')}`}</small>
          <strong lang={dest.localLanguage || undefined}>{dest.localName}</strong>
          <span lang={dest.localLanguage || undefined}>{dest.localAddress}</span>
        </button>
      )}

      <div className="tb-row" style={{ alignItems: 'center' }}>
        {data.links.uber && <a className="tb-link" href={data.links.uber} target="_blank" rel="noopener noreferrer">{t('route.uber')}</a>}
        {data.links.uber && <span className="tb-note">{t('route.uberNote')}</span>}
      </div>
      {data.fetchedAt && <div className="tb-note">{t('route.checked', { source: data.source, time: formatDateTime(data.fetchedAt) })}</div>}
    </div>
  );
}

function PlanSection({ ctx, state, update }) {
  const { run, busy, error } = useTool(ctx);
  const [request, setRequest] = useState('');
  const [draft, setDraft] = useState(null);
  const [replanDate, setReplanDate] = useState('');
  const [replanReq, setReplanReq] = useState('');

  const save = (itinerary) => update({ itinerary, itineraryHistory: [...state.itineraryHistory, state.itinerary].filter(Boolean).slice(-10) });
  const undo = () => {
    const prev = state.itineraryHistory.at(-1);
    if (prev) update({ itinerary: prev, itineraryHistory: state.itineraryHistory.slice(0, -1) });
  };

  const generate = async () => {
    const result = await run('plan', { request: request || 'Plan the trip' });
    if (result?.days) setDraft(result);
  };

  const replan = async () => {
    const result = await run('replan', { date: replanDate, request: replanReq, itinerary: state.itinerary });
    if (result?.day) {
      const days = state.itinerary.days.map((d) => (d.date === result.day.date ? result.day : d));
      setDraft({ ...state.itinerary, days, changes: result.changes });
    }
  };

  return (
    <Section id="plan" kicker={t('kicker.companion')} desc={t('plan.desc')}>
      <div className="tb-row">
        <Field id="plan-req" label={t('plan.request')} value={request} onChange={setRequest} placeholder={t('plan.requestPh')} />
        <button className="tb-btn" onClick={generate} disabled={!state.trip.destination || busy}>{busy ? t('common.working') : t('plan.generate')}</button>
      </div>
      {!state.trip.destination && <div className="tb-warn">{t('plan.needDestination')}</div>}
      {error && <div className="tb-err" role="alert">{error}</div>}

      {draft && (
        <div className="tb-panel">
          <strong>{t('plan.draft')}{draft.changes ? ` — ${draft.changes}` : ''}</strong>
          <DayList days={draft.days} />
          {draft.toVerify?.length > 0 && <div className="tb-warn">{t('plan.toVerify', { items: join(draft.toVerify) })}</div>}
          <div className="tb-row" style={{ marginTop: 12 }}>
            <button className="tb-gold" onClick={() => { save(draft); setDraft(null); }}>{t('common.accept')}</button>
            <button className="tb-ghost" onClick={() => setDraft(null)}>{t('common.discard')}</button>
          </div>
        </div>
      )}

      {state.itinerary && (
        <>
          <div className="tb-row" style={{ justifyContent: 'space-between', marginTop: 18 }}>
            <div className="tb-sub" style={{ margin: 0 }}>{t('plan.saved')}</div>
            <button className="tb-ghost" onClick={undo} disabled={!state.itineraryHistory.length}>{t('plan.undo')}</button>
          </div>
          <DayList days={state.itinerary.days} routes />
          <div className="tb-sub">{t('plan.adjust')}</div>
          <div className="tb-row">
            <Select id="rp-date" label={t('plan.day')} value={replanDate} onChange={setReplanDate} options={[['', t('plan.pickDay')], ...state.itinerary.days.map((d) => [d.date, formatDate(d.date)])]} />
            <Field id="rp-req" label={t('plan.change')} value={replanReq} onChange={setReplanReq} placeholder={t('plan.changePh')} />
            <button className="tb-btn" onClick={replan} disabled={!replanDate || !replanReq || busy}>{t('plan.replan')}</button>
          </div>
        </>
      )}
    </Section>
  );
}

const BLANK_BOOKING = { type: 'hotel', name: '', start: '', end: '', timezone: '', address: '', phone: '', confirmation: '' };
const BOOKING_TYPES = ['flight', 'hotel', 'car', 'restaurant', 'activity'];

function BookingsSection({ ctx, state, update }) {
  const { run, busy, error, setError } = useTool(ctx);
  const [text, setText] = useState('');
  const [found, setFound] = useState([]);
  const [manual, setManual] = useState(BLANK_BOOKING);
  const [dupes, setDupes] = useState(0);
  const [editing, setEditing] = useState(null);
  const [draftB, setDraftB] = useState(BLANK_BOOKING);

  const add = (list) => {
    const known = new Set(state.bookings.map((b) => b.confirmation).filter(Boolean));
    const fresh = list.filter((b) => !b.confirmation || !known.has(b.confirmation));
    update({ bookings: [...state.bookings, ...fresh.map(({ uncertain, ...b }) => ({ ...b, source: 'user-reviewed' }))] });
    setDupes(list.length - fresh.length);
  };

  const extract = async (image) => {
    const result = await run('extractBooking', { text }, image ? { image } : {});
    if (result?.bookings) setFound(result.bookings);
  };

  return (
    <Section id="bookings" kicker={t('kicker.companion')} desc={t('bookings.desc')}>
      <label className="tb-label" htmlFor="b-text">{t('bookings.text')}</label>
      <textarea id="b-text" className="tb-input" rows={3} dir="auto" value={text} onChange={(e) => setText(e.target.value)} placeholder={t('bookings.textPh')} />
      <div className="tb-row" style={{ marginTop: 10 }}>
        <button className="tb-btn" onClick={() => extract()} disabled={!text.trim() || busy}>{busy ? t('common.working') : t('bookings.extract')}</button>
        <FileButton label={t('bookings.upload')} onFile={async (f) => { try { extract(await readImage(f)); } catch (e) { setError(e.message); } }} />
      </div>
      {error && <div className="tb-err" role="alert">{error}</div>}
      {found.length > 0 && (
        <div className="tb-panel">
          <strong>{t('bookings.review')}</strong>
          {found.map((b, i) => (
            <div key={i} className="tb-item">
              {label('bookingType', b.type)} · <strong>{b.name}</strong> · <Ltr>{b.start}{b.end ? ` → ${b.end}` : ''}</Ltr> · <Ltr>#{b.confirmation || '—'}</Ltr>
              {b.phone && <> · <Ltr>{b.phone}</Ltr></>}
              {b.uncertain?.length > 0 && <div className="tb-note" style={{ color: '#8a5a00' }}>{t('bookings.check', { items: b.uncertain.join(', ') })}</div>}
            </div>
          ))}
          <div className="tb-row" style={{ marginTop: 10 }}>
            <button className="tb-gold" onClick={() => { add(found); setFound([]); }}>{t('common.saveAll')}</button>
            <button className="tb-ghost" onClick={() => setFound([])}>{t('common.discard')}</button>
          </div>
        </div>
      )}

      <div className="tb-sub">{t('bookings.manual')}</div>
      <div className="tb-row">
        <Select id="b-type" label={t('bookings.type')} value={manual.type} onChange={(v) => setManual({ ...manual, type: v })} options={BOOKING_TYPES.map((ty) => [ty, label('bookingType', ty)])} />
        <Field id="b-name" label={t('bookings.name')} value={manual.name} onChange={(v) => setManual({ ...manual, name: v })} />
      </div>
      <div className="tb-row">
        <Field id="b-start" label={t('bookings.start')} type="datetime-local" value={manual.start} onChange={(v) => setManual({ ...manual, start: v })} />
        <Field id="b-conf" label={t('bookings.confirmation')} value={manual.confirmation} onChange={(v) => setManual({ ...manual, confirmation: v })} dir="ltr" />
        <button className="tb-btn" disabled={!manual.name} onClick={() => { add([manual]); setManual(BLANK_BOOKING); }}>{t('common.add')}</button>
      </div>
      {dupes > 0 && <div className="tb-warn">{t('bookings.duplicates', { count: dupes })}</div>}

      <div className="tb-sub">{t('bookings.saved', { n: state.bookings.length })}</div>
      {state.bookings.length === 0 && <p className="tb-note">{t('common.noneYet')}</p>}
      {state.bookings.map((b, i) => (editing === i ? (
        <div key={i} className="tb-panel">
          <div className="tb-row">
            <Select id={`be-type-${i}`} label={t('bookings.type')} value={draftB.type} onChange={(v) => setDraftB({ ...draftB, type: v })} options={[...BOOKING_TYPES, 'other'].map((ty) => [ty, label('bookingType', ty)])} />
            <Field id={`be-name-${i}`} label={t('bookings.name')} value={draftB.name} onChange={(v) => setDraftB({ ...draftB, name: v })} />
          </div>
          <div className="tb-row">
            <Field id={`be-start-${i}`} label={t('bookings.start')} type="datetime-local" value={(draftB.start || '').slice(0, 16)} onChange={(v) => setDraftB({ ...draftB, start: v })} />
            <Field id={`be-phone-${i}`} label={t('bookings.phone')} value={draftB.phone} onChange={(v) => setDraftB({ ...draftB, phone: v })} dir="ltr" inputMode="tel" />
          </div>
          <div className="tb-row">
            <Field id={`be-conf-${i}`} label={t('bookings.confirmation')} value={draftB.confirmation} onChange={(v) => setDraftB({ ...draftB, confirmation: v })} dir="ltr" />
            <Field id={`be-party-${i}`} label={t('bookings.party')} value={draftB.party} onChange={(v) => setDraftB({ ...draftB, party: v })} />
          </div>
          <div className="tb-row" style={{ marginTop: 10 }}>
            <button className="tb-btn" disabled={!draftB.name} onClick={() => { update({ bookings: state.bookings.map((x, j) => (j === i ? draftB : x)) }); setEditing(null); }}>{t('common.save')}</button>
            <button className="tb-ghost" onClick={() => setEditing(null)}>{t('common.cancel')}</button>
          </div>
        </div>
      ) : (
        <div key={i} className="tb-item" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
          <span>
            {label('bookingType', b.type)} · <strong>{b.name}</strong> · {b.start ? formatDateTime(b.start) : '—'} · <Ltr>#{b.confirmation || '—'}</Ltr>
            {b.phone && <> · <Ltr>{b.phone}</Ltr></>}{' '}
            <span className="tb-note">({label('source', b.source)})</span>
          </span>
          <span className="tb-row" style={{ flexWrap: 'nowrap' }}>
            <button className="tb-ghost" onClick={() => { setEditing(i); setDraftB({ ...BLANK_BOOKING, party: '', ...b }); }}>{t('bookings.edit')}</button>
            <button className="tb-ghost" onClick={() => update({ bookings: state.bookings.filter((_, j) => j !== i) })}>{t('common.remove')}</button>
          </span>
        </div>
      )))}
    </Section>
  );
}

function TranslateSection({ ctx }) {
  const { run, busy, error, setError } = useTool(ctx);
  const [text, setText] = useState('');
  const [target, setTarget] = useState('ja');
  const [result, setResult] = useState(null);

  const go = async (image) => {
    const r = await run('translate', { text, target: languageOf(target).name }, image ? { image } : {});
    if (r) setResult(r);
  };

  return (
    <Section id="translate" kicker={t('kicker.companion')} desc={t('translate.desc')}>
      <label className="tb-label" htmlFor="tr-text">{t('translate.text')}</label>
      <textarea id="tr-text" className="tb-input" rows={3} dir="auto" value={text} onChange={(e) => setText(e.target.value.slice(0, 2000))} />
      <div className="tb-row">
        <Select id="tr-target" label={t('translate.into')} value={target} onChange={setTarget} options={targetLanguages()} />
        <button className="tb-btn" onClick={() => go()} disabled={!text.trim() || busy}>{busy ? t('common.working') : t('translate.go')}</button>
        <FileButton label={t('translate.photo')} onFile={async (f) => { try { go(await readImage(f)); } catch (e) { setError(e.message); } }} />
      </div>
      {error && <div className="tb-err" role="alert">{error}</div>}
      {result && (
        <div style={{ marginTop: 14 }}>
          <p className="tb-note">{t('translate.detected', { language: result.sourceLanguage })}</p>
          {(result.blocks || []).map((b, i) => (
            <div key={i} className="tb-item" style={{ display: 'flex', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="tb-note" dir="auto">{b.original}</div>
                <div style={{ fontWeight: 600 }} lang={target} dir="auto">
                  {b.translation} {b.lowConfidence && <span className="tb-note" style={{ color: '#8a5a00' }}>{t('translate.lowConfidence')}</span>}
                </div>
              </div>
              <button className="tb-ghost" aria-label={t('common.play', { text: b.translation })} onClick={() => speak(b.translation, target)}>🔊</button>
            </div>
          ))}
          {result.notes && <div className="tb-warn">{result.notes}</div>}
        </div>
      )}
    </Section>
  );
}

const HOTEL_REQUESTS = ['roomService', 'lateCheckout', 'restaurant', 'luggage'];

function HotelSection({ ctx, state }) {
  const { run, busy, error } = useTool(ctx);
  const [request, setRequest] = useState('lateCheckout');
  const [details, setDetails] = useState('');
  const [language, setLanguage] = useState('ja');
  const [msg, setMsg] = useState(null);
  const [calling, setCalling] = useState(false);

  const english = tEn(`hotelReq.${request}`);
  const write = async () => {
    const r = await run('hotel', { request: english, details, language: languageOf(language).name });
    if (r) setMsg(r);
  };

  const { hotelName, hotelPhone, travelerName, roomNumber } = state.trip;
  const callText = `Call ${hotelName || 'my hotel'}${hotelPhone ? ` at ${hotelPhone}` : ''} about: ${english}. ${details} Guest name: ${travelerName || 'unknown'}.${roomNumber ? ` Room ${roomNumber}.` : ''}`;

  return (
    <Section id="hotel" kicker={t('kicker.companion')} desc={t('hotel.desc')}>
      <div className="tb-row">
        {HOTEL_REQUESTS.map((r) => (
          <button key={r} className="tb-chip" aria-pressed={r === request} onClick={() => setRequest(r)}>{t(`hotelReq.${r}`)}</button>
        ))}
      </div>
      <div className="tb-row">
        <Field id="h-det" label={t('hotel.details')} value={details} onChange={setDetails} placeholder={t('hotel.detailsPh')} />
        <Select id="h-lang" label={t('hotel.staffLanguage')} value={language} onChange={setLanguage} options={targetLanguages()} />
      </div>
      <div className="tb-row" style={{ marginTop: 12 }}>
        <button className="tb-btn" onClick={write} disabled={busy}>{busy ? t('common.working') : t('hotel.write')}</button>
        <button className="tb-gold" disabled={!hotelPhone} onClick={() => setCalling(true)}>📞 {t('hotel.call')}</button>
      </div>
      {!hotelPhone && <div className="tb-warn">{t('hotel.needPhone')}</div>}
      {error && <div className="tb-err" role="alert">{error}</div>}
      {msg && (
        <div className="tb-panel">
          <div className="tb-big" lang={language} dir="auto">{msg.local}</div>
          <div className="tb-note" style={{ marginTop: 4 }} dir="auto">{msg.english}</div>
          <button className="tb-ghost" style={{ marginTop: 10 }} onClick={() => speak(msg.local, language)}>🔊 {t('hotel.play')}</button>
        </div>
      )}
      {calling && <div className="tb-panel"><CallReview instructions={callText} travelerName={travelerName} autoPlan /></div>}
    </Section>
  );
}

const LOCAL_CATEGORIES = ['dining', 'shopping', 'exploration', 'transport', 'lost', 'delay', 'urgent'];

function LocalSection({ ctx, state }) {
  const { run, busy, error } = useTool(ctx);
  const [category, setCategory] = useState('dining');
  const [request, setRequest] = useState('');
  const [result, setResult] = useState(null);

  const go = async () => {
    const r = await run('local', { category: tEn(`localCat.${category}`), area: state.trip.area || state.trip.destination, request });
    if (r) setResult(r);
  };

  return (
    <Section id="local" kicker={t('kicker.companion')} desc={t('local.desc')}>
      <div className="tb-row">
        {LOCAL_CATEGORIES.map((c) => (
          <button key={c} className="tb-chip" aria-pressed={c === category} onClick={() => setCategory(c)}>{t(`localCat.${c}`)}</button>
        ))}
      </div>
      <div className="tb-row">
        <Field id="l-req" label={t('local.request')} value={request} onChange={setRequest} placeholder={t('local.requestPh')} />
        <button className="tb-btn" onClick={go} disabled={busy}>{busy ? t('common.working') : t('local.ask')}</button>
      </div>
      {error && <div className="tb-err" role="alert">{error}</div>}
      {result && (
        <div style={{ marginTop: 12 }}>
          {(result.suggestions || []).map((x, i) => (
            <div key={i} className="tb-item">
              <strong>{x.name}</strong> <span className="tb-note">{join([x.area, x.price].filter(Boolean))}</span>
              <div>{x.why}</div>
              {x.verify && <div className="tb-note" style={{ color: '#8a5a00' }}>{t('local.verify', { text: x.verify })}</div>}
            </div>
          ))}
          {result.localPhrase && <div className="tb-warn">{t('local.phrase', { text: result.localPhrase })}</div>}
        </div>
      )}
    </Section>
  );
}

function ReadySection({ ctx }) {
  const { run, busy, error } = useTool(ctx);
  const [result, setResult] = useState(null);
  const [done, setDone] = useState({});
  return (
    <Section id="ready" kicker={t('kicker.companion')} desc={t('ready.desc')}>
      <button className="tb-btn" onClick={async () => setResult(await run('ready'))} disabled={busy}>{busy ? t('common.working') : t('ready.build')}</button>
      {error && <div className="tb-err" role="alert">{error}</div>}
      {result && (
        <div style={{ marginTop: 12 }}>
          {(result.warnings || []).map((w, i) => <div key={i} className="tb-warn">{w}</div>)}
          {(result.items || []).map((x, i) => (
            <label key={i} className="tb-item" style={{ display: 'flex', gap: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!done[i]} onChange={(e) => setDone({ ...done, [i]: e.target.checked })} style={{ width: 20, height: 20, marginTop: 3 }} />
              <span>
                <span className="tb-kicker" style={{ color: '#5b6680' }}>{x.category}</span>
                <div style={{ fontWeight: 600, textDecoration: done[i] ? 'line-through' : 'none' }}>{x.item}</div>
                <div className="tb-note">{x.why}</div>
              </span>
            </label>
          ))}
        </div>
      )}
    </Section>
  );
}

function FoodSection({ ctx }) {
  const { run, busy, error, setError } = useTool(ctx);
  const [photo, setPhoto] = useState('');
  const [result, setResult] = useState(null);
  const [portion, setPortion] = useState(1);

  const onFile = async (file) => {
    setResult(null);
    try {
      const image = await readImage(file);
      setPhoto(image);
      setPortion(1);
      const r = await run('calories', { portion: 1 }, { image });
      if (r) setResult(r);
    } catch (e) {
      setError(e.message);
    }
  };

  const k = (n) => formatNumber(Math.round(((Number(n) || 0) * portion) / 10) * 10);

  return (
    <Section id="food" kicker={t('kicker.companion')} desc={t('food.desc')}>
      <FileButton label={`📷 ${t('food.snap')}`} className="tb-btn" capture="environment" onFile={onFile} />
      {error && <div className="tb-err" role="alert">{error}</div>}
      {photo && <img src={photo} alt={t('food.alt')} style={{ display: 'block', maxWidth: 320, width: '100%', borderRadius: 12, marginTop: 12 }} />}
      {busy && <p className="tb-note">{t('food.analyzing')}</p>}
      {result && !result.isFood && <div className="tb-warn">{t('food.notFood')} {result.question || t('food.tryAgain')}</div>}
      {result?.isFood && (
        <div className="tb-panel">
          <span className="tb-kicker">{t('food.estimated')}</span>
          <div style={{ fontSize: 30, fontWeight: 750 }}>{t('food.total', { kcal: k(result.totalKcal) })}</div>
          <div className="tb-note">{t('food.range', { low: k(result.low), high: k(result.high) })}{portion !== 1 ? ` · ${t('food.adjusted')}` : ''}</div>
          <div className="tb-row" style={{ margin: '12px 0' }}>
            {[[0.5, 'food.half'], [1, 'food.all'], [1.5, 'food.oneHalf']].map(([p, key]) => (
              <button key={p} className="tb-chip" aria-pressed={p === portion} onClick={() => setPortion(p)}>{t(key)}</button>
            ))}
          </div>
          {(result.dishes || []).map((d, i) => (
            <div key={i} className="tb-item">{t('food.dish', { name: d.name, kcal: k(d.kcal) })} <span className="tb-note">({k(d.low)}–{k(d.high)})</span></div>
          ))}
          <p className="tb-note">{t('food.assumed', { text: result.portionAssumption })} {result.uncertainty}</p>
        </div>
      )}
    </Section>
  );
}

function StatusSection() {
  const counts = STATUS.reduce((a, [, st]) => ({ ...a, [st]: (a[st] || 0) + 1 }), {});
  return (
    <Section id="status" kicker={t('kicker.progress')} desc={t('status.summary', { done: counts.done || 0, partial: counts.partial || 0, missing: counts.missing || 0 })}>
      {STATUS.map(([name, st, note]) => (
        <div key={name} className="tb-item" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span className={`tb-badge ${st}`}>{t(`status.${st}`)}</span>
          <div lang="en" dir="ltr">
            <div style={{ fontWeight: 600 }}>{name}</div>
            <div className="tb-note">{note}</div>
          </div>
        </div>
      ))}
    </Section>
  );
}

/* ---------- Get connected: data plan + Travel Number checkout (demo: nothing is charged) ---------- */

const ESIM_REGIONS = [
  { id: 'japan', match: /japan|kyoto|osaka|tokyo|nara/i, countries: 1 },
  { id: 'asia', match: /korea|seoul|busan|taiwan/i, countries: 13 },
  { id: 'sea', match: /thailand|bangkok|singapore|malaysia|vietnam|indonesia|bali|philippines/i, countries: 8 },
  { id: 'china', match: /china|shanghai|beijing|hong ?kong|macau/i, countries: 3 },
];
// Shown until the server's catalog (GET /api/p3/billing) arrives; prices always come from the server.
// Shown until the server's catalog (GET /api/p3/billing) arrives; prices always come from the server.
// Shown until the server's catalog (GET /api/p3/billing) arrives; prices always come from the server.
const ESIM_SIZES = [
  { gb: 3, days: 7, cents: 900 },
  { gb: 5, days: 15, cents: 0, included: true },
  { gb: 10, days: 30, cents: 1800 },
  { gb: 20, days: 30, cents: 2900 },
];
// Milo's real Twilio number is the only one on offer; the other countries are shown but not available yet.
const NUMBER_COUNTRIES = [
  { id: 'us', cc: '+1', flag: '🇺🇸', voice: true, sms: true, verify: false },
  ...[['jp', '🇯🇵', '+81'], ['hk', '🇭🇰', '+852'], ['sg', '🇸🇬', '+65'], ['th', '🇹🇭', '+66'], ['kr', '🇰🇷', '+82'], ['my', '🇲🇾', '+60'], ['ph', '🇵🇭', '+63'], ['id', '🇮🇩', '+62'], ['vn', '🇻🇳', '+84'], ['cn', '🇨🇳', '+86']]
    .map(([id, flag, cc]) => ({ id, flag, cc, soon: true })),
];
const countryName = (id) => { try { return new Intl.DisplayNames([locale()], { type: 'region' }).of(id.toUpperCase()); } catch { return id.toUpperCase(); } };

/** Data eSIM checkout: plan → phone check → pay by card (the included plan is free, once). */
function DataWizard({ state, device, covered, onBuy }) {
  const dest = state.trip.destination || '';
  const [step, setStep] = useState(0);
  const [region, setRegion] = useState((ESIM_REGIONS.find((r) => r.match.test(dest)) || ESIM_REGIONS[1]).id);
  const [size, setSize] = useState(5);
  const [confirmed, setConfirmed] = useState(false);
  const [terms, setTerms] = useState(false);
  const [paying, setPaying] = useState(false);
  const [card, setCard] = useState('');
  const [error, setError] = useState('');
  const { billing, reload } = useBilling(requestJson, api);
  const pay = usePay(requestJson, api);
  const sizes = billing?.esim.plans || ESIM_SIZES;
  const usedUp = (s) => s.included && billing?.esim.includedUsed;
  const plan = sizes.find((s) => s.gb === size) || sizes[0];
  const steps = ['data', 'device', 'pay'];

  const buy = async () => {
    setPaying(true);
    setError('');
    try {
      await pay('billing/esim', { region, gb: plan.gb }, plan.cents ? card : '');
      onBuy({ region, gb: plan.gb, days: plan.days, price: plan.cents / 100, total: plan.cents / 100, pay: plan.cents ? 'card' : 'included' });
    } catch (e) {
      setError(e.message);
      setPaying(false);
      reload().catch(() => {});
    }
  };
  const canNext = [!usedUp(plan), confirmed && covered, terms][step];

  return (
    <div className="tb-wizard">
      <ol className="tb-steps" aria-label={t('esim.progress')}>
        {steps.map((k, i) => <li key={k} aria-current={i === step ? 'step' : undefined} className={i < step ? 'done' : ''}>{t(`shop.step.${k}`)}</li>)}
      </ol>

      {step === 0 && (
        <>
          <div className="tb-sub">{t('shop.where')}</div>
          <div className="tb-choices">
            {ESIM_REGIONS.map((r) => (
              <button key={r.id} className="tb-choice" aria-pressed={region === r.id} onClick={() => setRegion(r.id)}>
                <strong>{t(`shop.region.${r.id}`)}</strong>
                <small>{t('shop.countries', { count: r.countries })}</small>
              </button>
            ))}
          </div>
          {region === 'china' && <p className="tb-note">🌐 {t('shop.chinaNote')}</p>}
          <div className="tb-sub">{t('shop.howMuch')}</div>
          <div className="tb-choices">
            {sizes.map((s) => (
              <button key={s.gb} className="tb-choice" aria-pressed={size === s.gb} disabled={usedUp(s)} onClick={() => setSize(s.gb)}>
                <strong className="tb-gb">{s.gb} GB</strong>
                <small>{t('shop.days', { count: s.days })}</small>
                <span className={`tb-price${s.included ? ' inc' : ''}`}>{usedUp(s) ? t('pay.includedUsed') : s.included ? t('shop.included') : cents(s.cents)}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <div className="tb-sub">{t('esim.compat')}</div>
          <div className={covered ? 'tb-ok' : 'tb-err'} style={{ marginTop: 0 }}>
            {dest ? (covered ? t('esim.covered', { place: dest }) : t('esim.notCovered', { place: dest })) : t('esim.noDestination')}
          </div>
          <p className="tb-note">{t(`esim.device.${device}`)}</p>
          <label className="tb-item" style={{ display: 'flex', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ width: 20, height: 20 }} />
            <span>{t('esim.confirmDevice')}<div className="tb-note">{t('esim.chinaNote')}</div></span>
          </label>
        </>
      )}

      {step === 2 && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div className="tb-sub" style={{ margin: 0 }}>{t('shop.summary')}</div>
          <dl className="tb-facts tb-receipt">
            <dt>{t('shop.dataLine', { region: t(`shop.region.${region}`), gb: plan.gb, days: plan.days })}</dt>
            <dd>{plan.included ? t('shop.included') : cents(plan.cents)}</dd>
            <dt className="tb-total">{t('shop.total')}</dt>
            <dd className="tb-total">{cents(plan.cents)}</dd>
          </dl>
          {plan.cents > 0 && (payReady ? <CardPicker billing={billing} card={card} setCard={setCard} onChanged={reload} requestJson={requestJson} api={api} /> : <p className="tb-note">{t('pa.pay.notSetUp')}</p>)}
          <label className="tb-note" style={{ display: 'flex', gap: 8 }}>
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
            <span>{t('esim.terms', { days: plan.days })}</span>
          </label>
          <p className="tb-note" style={{ margin: 0 }}>🧪 {t('pay.esimNote')}</p>
          {error && <div className="tb-err" role="alert" style={{ margin: 0 }}>{error}</div>}
        </div>
      )}

      <WizardNav step={step} last={2} setStep={setStep} canNext={canNext} paying={paying} terms={terms && (!plan.cents || payReady)} total={plan.cents} onPay={buy} />
    </div>
  );
}

function WizardNav({ step, last, setStep, canNext, paying, terms, total, onPay }) {
  return (
    <div className="tb-row" style={{ marginTop: 14, justifyContent: 'space-between' }}>
      {step > 0 ? <button className="tb-ghost" onClick={() => setStep(step - 1)} disabled={paying}>{t('shop.back')}</button> : <span />}
      {step < last ? (
        <button className="tb-btn" disabled={!canNext} onClick={() => setStep(step + 1)}>{t('shop.next')} <Arrow /></button>
      ) : (
        <button className="tb-gold" disabled={!terms || paying} onClick={onPay}>
          {paying ? <><span className="tb-live" aria-hidden="true" />{t('shop.paying')}</> : total > 0 ? t('shop.payAmount', { amount: cents(total) }) : t('shop.confirmFree')}
        </button>
      )}
    </div>
  );
}

/** Travel Number checkout: country and weekly or monthly rental → pay by card (a subscription until stopped). */
function NumberWizard({ onBuy }) {
  const [step, setStep] = useState(0);
  const country = 'us';
  const [plan, setPlan] = useState('month');
  const [terms, setTerms] = useState(false);
  const [paying, setPaying] = useState(false);
  const [card, setCard] = useState('');
  const [error, setError] = useState('');
  const { billing, reload } = useBilling(requestJson, api);
  const pay = usePay(requestJson, api);
  const plans = billing?.number.plans || [];
  const price = plans.find((p) => p.plan === plan)?.cents || 0;
  const per = (p) => t(p.plan === 'week' ? 'pay.perWeek' : 'pay.perMonth', { price: cents(p.cents) });
  const steps = ['number', 'pay'];

  const buy = async () => {
    setPaying(true);
    setError('');
    try {
      onBuy(await pay('numbers/travel', { plan }, card));
    } catch (e) {
      setError(e.message);
      setPaying(false);
      reload().catch(() => {});
    }
  };

  return (
    <div className="tb-wizard">
      <ol className="tb-steps" aria-label={t('title.number')}>
        {steps.map((k, i) => <li key={k} aria-current={i === step ? 'step' : undefined} className={i < step ? 'done' : ''}>{t(`shop.step.${k}`)}</li>)}
      </ol>

      {step === 0 && (
        <>
          <div className="tb-sub">{t('shop.numberTitle')}</div>
          <p className="tb-note" style={{ marginTop: 0 }}>{t('shop.numberWhy')}</p>
          <div className="tb-choices">
            {NUMBER_COUNTRIES.map((c) => (
              <button key={c.id} className="tb-choice" aria-pressed={country === c.id} disabled={c.soon}>
                <strong>{c.flag} {countryName(c.id)} <Ltr>{c.cc}</Ltr></strong>
                <small>{c.soon ? t('shop.soon') : join([c.voice && t('shop.calls'), c.sms && t('shop.sms'), t('shop.instant')].filter(Boolean))}</small>
              </button>
            ))}
          </div>
          <div className="tb-sub">{t('pay.howLong')}</div>
          <div className="tb-choices">
            {plans.map((p) => (
              <button key={p.plan} className="tb-choice" aria-pressed={plan === p.plan} onClick={() => setPlan(p.plan)}>
                <strong>{t(`pay.${p.plan}`)}</strong>
                <span className="tb-price">{per(p)}</span>
              </button>
            ))}
          </div>
          <p className="tb-note">{t('shop.numberAssigned')}</p>
        </>
      )}

      {step === 1 && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div className="tb-sub" style={{ margin: 0 }}>{t('shop.summary')}</div>
          <dl className="tb-facts tb-receipt">
            <dt>{t('shop.numberLine', { country: countryName(country) })}</dt>
            <dd>{per({ plan, cents: price })}</dd>
            <dt className="tb-total">{t('shop.total')}</dt>
            <dd className="tb-total">{cents(price)}</dd>
          </dl>
          {payReady ? <CardPicker billing={billing} card={card} setCard={setCard} onChanged={reload} requestJson={requestJson} api={api} /> : <p className="tb-note">{t('pa.pay.notSetUp')}</p>}
          <label className="tb-note" style={{ display: 'flex', gap: 8 }}>
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
            <span>{t(plan === 'week' ? 'pay.termsWeek' : 'pay.termsMonth')}</span>
          </label>
          {error && <div className="tb-err" role="alert" style={{ margin: 0 }}>{error}</div>}
        </div>
      )}

      <WizardNav step={step} last={1} setStep={setStep} canNext={price > 0} paying={paying} terms={terms && payReady} total={price} onPay={buy} />
    </div>
  );
}

/** The traveler's Travel Number card (demo): verification, then active with copy. */
function TravelNumberCard({ number, onVerify }) {
  const [copied, setCopied] = useState(false);
  const c = NUMBER_COUNTRIES.find((x) => x.id === number.country);
  return (
    <div className="tb-numcard">
      <div>
        <small>{t('shop.yourNumber')}</small>
        <strong className="tb-numbig"><Ltr>{number.number}</Ltr></strong>
        <div className="tb-note">{c?.flag} {join([countryName(number.country), number.voice && t('shop.calls'), number.sms && t('shop.sms')].filter(Boolean))}</div>
      </div>
      {number.status === 'verify' ? (
        <div style={{ display: 'grid', gap: 6, justifyItems: 'end' }}>
          <span className="tb-badge partial" style={{ padding: '3px 10px' }}>{t('shop.status.verify')}</span>
          <button className="tb-btn" onClick={onVerify}>🪪 {t('shop.verifyNow')}</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 6, justifyItems: 'end' }}>
          <span className="tb-badge done" style={{ padding: '3px 10px' }}>{t('shop.status.active')}</span>
          <button className="tb-ghost" onClick={async () => { try { await navigator.clipboard.writeText(number.number); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ } }}>
            {copied ? '✓' : t('shop.copy')}
          </button>
        </div>
      )}
    </div>
  );
}

/** Travel Number: a local phone number for calls and texts, separate from the data eSIM (demo). */
/** The account's travel number from the pool, as the app shows it. */
export const travelNumberState = (number, capabilities = {}) => (number
  ? { number, country: 'us', voice: capabilities.voice !== false, sms: capabilities.sms !== false, status: 'active' }
  : null);

function TravelNumberSection({ state, update }) {
  const [error, setError] = useState('');
  const { billing, reload } = useBilling(requestJson, api);
  const rental = billing?.number.rental;
  const number = state.number;
  const until = rental?.periodEnd ? formatDate(rental.periodEnd) : '';
  const bought = (r) => {
    if (r.number) update({ number: { ...travelNumberState(r.number), pay: 'card', boughtAt: new Date().toISOString() } });
    reload().catch(() => {});
  };
  const release = async () => {
    if (!window.confirm(until ? t('pay.stopConfirm', { date: until }) : t('number.releaseConfirm'))) return;
    try {
      const r = await requestJson(api('numbers/travel'), { method: 'DELETE' });
      if (!r.number) update({ number: null });
      await reload();
    } catch (e) {
      setError(e.message);
    }
  };
  return (
    <Section id="number" kicker={t('kicker.tools')} desc={t('number.desc')}>
      {number ? (
        <>
          <TravelNumberCard number={number} onVerify={() => update({ number: { ...number, status: 'active' } })} />
          {until && <p className="tb-note">{t(`pay.${rental.plan}`)} · {t(rental.cancelAtPeriodEnd ? 'pay.ends' : 'pay.renews', { date: until })}</p>}
          {!rental?.cancelAtPeriodEnd && <button className="tb-link" style={{ marginTop: 12 }} onClick={release}>{t('number.reset')}</button>}
        </>
      ) : (
        <NumberWizard onBuy={bought} />
      )}
      {error && <div className="tb-err" role="alert">{error}</div>}
    </Section>
  );
}

/* ---------- eSIM (demo: no real profile is issued) ---------- */

const ESIM_PLAN = { gb: 5, validityDays: 15, coverage: ['japan', 'korea', 'china', 'hong kong', 'taiwan', 'thailand', 'singapore', 'malaysia', 'vietnam', 'indonesia', 'philippines', 'bali', 'kyoto', 'osaka', 'tokyo', 'seoul', 'bangkok', 'shanghai', 'beijing'] };

function deviceKind() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

/** A made-up but correctly shaped eSIM activation (LPA) for the demo. */
function demoActivation() {
  const code = Array.from({ length: 4 }, () => Math.random().toString(36).slice(2, 6).toUpperCase()).join('-');
  const smdp = 'smdp.demo.xcards.example';
  return { smdp, code: `DEMO-${code}`, lpa: `LPA:1$${smdp}$DEMO-${code}`, iccid: `8988 ${String(Math.random()).slice(2, 6)} ${String(Math.random()).slice(2, 6)} ${String(Math.random()).slice(2, 6)}` };
}

/** Try a real install: paste a provider's activation code, or use the public test profile. */
function RealEsimTest({ onUse }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const use = (value) => {
    const parsed = parseLpa(value);
    if (!parsed) return setError(t('esim.testInvalid'));
    setError('');
    return onUse({ ...parsed, iccid: '' });
  };
  return (
    <details className="tb-panel">
      <summary style={{ cursor: 'pointer', fontWeight: 700 }}>📲 {t('esim.testTitle')}</summary>
      <p className="tb-note">{t('esim.testDesc')}</p>
      <Field id="esim-real" label={t('esim.testInput')} value={code} onChange={setCode} dir="ltr" placeholder="LPA:1$…" autoComplete="off" spellCheck={false} />
      <div className="tb-row" style={{ marginTop: 8 }}>
        <button className="tb-btn" disabled={!code.trim()} onClick={() => use(code)}>{t('esim.testUse')}</button>
        <button className="tb-ghost" onClick={() => use(PUBLIC_TEST_LPA)}>{t('esim.testPublic')}</button>
      </div>
      {error && <div className="tb-err" role="alert">{error}</div>}
    </details>
  );
}

function ESimSection({ state, update }) {
  const esim = state.esim || { status: 'none' };
  const set = (patch) => update({ esim: { ...esim, ...patch } });
  const [qr, setQr] = useState('');
  const [reveal, setReveal] = useState(false);
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');
  const { billing } = useBilling(requestJson, api);
  const pay = usePay(requestJson, api);
  const device = deviceKind();
  const dest = (state.trip.destination || '').toLowerCase();
  const covered = !dest || ESIM_PLAN.coverage.some((c) => dest.includes(c));
  const planGb = esim.gb || ESIM_PLAN.gb;
  const planDays = esim.days || ESIM_PLAN.validityDays;
  const total = planGb + (esim.topupGb || 0);
  const used = esim.usedGb || 0;
  const pct = Math.min(100, Math.round((used / total) * 100));
  const iosLink = esim.lpa ? `https://esimsetup.apple.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(esim.lpa)}` : '';
  const androidLink = esim.lpa ? `https://esimsetup.android.com/esim_qrcode_provisioning?carddata=${encodeURIComponent(esim.lpa)}` : '';

  useEffect(() => {
    let cancelled = false;
    if (esim.lpa) QRCode.toDataURL(esim.lpa, { margin: 1, width: 220 }).then((url) => !cancelled && setQr(url)).catch(() => setQr(''));
    return () => { cancelled = true; };
  }, [esim.lpa]);

  const copy = async (what, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      setCopied('');
    }
  };

  const buy = (order) => {
    update({ esim: { status: 'provisioning', ...order } });
    setTimeout(() => update({ esim: { ...order, ...demoActivation(), status: 'ready', provisionedAt: new Date().toISOString(), usedGb: 0, topupGb: 0 } }), 1500);
  };
  const activate = () => {
    const now = Date.now();
    set({ status: 'active', activatedAt: new Date(now).toISOString(), expiresAt: new Date(now + planDays * 86400000).toISOString(), syncedAt: new Date(now).toISOString() });
  };
  const useData = () => {
    const next = Math.min(total, Math.round((used + 0.7) * 10) / 10);
    set({ usedGb: next, syncedAt: new Date().toISOString(), status: next >= total ? 'exhausted' : 'active' });
  };
  const topUp = async (gb, price) => {
    if (!window.confirm(t('pay.topupConfirm', { gb, price }))) return;
    setError('');
    try {
      await pay('billing/esim', { topupGb: gb }, '');
      set({ topupGb: (esim.topupGb || 0) + gb, status: 'active', syncedAt: new Date().toISOString() });
    } catch (e) {
      setError(e.message);
    }
  };

  const STEP = { none: 0, provisioning: 1, ready: 1, installed: 2, active: 3, exhausted: 3 };
  const step = STEP[esim.status] ?? 0;

  return (
    <Section id="esim" kicker={t('kicker.tools')} desc={t('esim.desc')}>
      {esim.real ? <div className="tb-warn" style={{ marginTop: 0 }}>📲 {t('esim.realNote')}</div> : <div className="tb-warn" style={{ marginTop: 0 }}>🧪 {t('esim.demo')}</div>}

      {esim.status !== 'none' && <div className="tb-esim-plan">
        <div>
          <strong>{esim.region ? t('shop.dataLine', { region: t(`shop.region.${esim.region}`), gb: planGb, days: planDays }) : t('esim.planName', { gb: planGb })}</strong>
          <div className="tb-note">{t('esim.planMeta', { days: planDays })}</div>
        </div>
        <span className={`tb-badge ${esim.status === 'active' ? 'done' : esim.status === 'exhausted' ? 'missing' : 'partial'}`} style={{ padding: '3px 12px', flex: 'none' }}>
          {t(`esim.status.${esim.status}`)}
        </span>
      </div>}

      {esim.status !== 'none' && <ol className="tb-steps" aria-label={t('esim.progress')}>
        {['check', 'install', 'connect', 'use'].map((k, i) => (
          <li key={k} aria-current={i === step ? 'step' : undefined} className={i < step ? 'done' : ''}>{t(`esim.step.${k}`)}</li>
        ))}
      </ol>}

      {esim.status === 'none' && <DataWizard state={state} device={device} covered={covered} onBuy={buy} />}
      {(esim.status === 'none' || esim.real) && <RealEsimTest onUse={(a) => update({ esim: { ...a, real: true, status: 'ready', gb: esim.gb, days: esim.days, region: esim.region, provisionedAt: new Date().toISOString(), usedGb: 0, topupGb: 0 } })} />}

      {esim.status === 'provisioning' && <p className="tb-note"><span className="tb-live" aria-hidden="true" />{t('esim.provisioning')}</p>}

      {esim.status === 'ready' && (
        <div className="tb-panel">
          <span className="tb-kicker">{t('esim.installTitle')}</span>
          <p className="tb-note" style={{ marginTop: 4 }}>{t('esim.wifiFirst')}</p>
          {device === 'ios' && <a className="tb-btn" style={{ textDecoration: 'none' }} href={iosLink}>{t('esim.installIos')}</a>}
          {device === 'android' && <a className="tb-btn" style={{ textDecoration: 'none' }} href={androidLink} target="_blank" rel="noopener noreferrer">{t('esim.installAndroid')}</a>}
          <ol className="tb-howto">
            {[1, 2, 3].map((n) => <li key={n}>{t(`esim.${device === 'android' ? 'android' : 'ios'}${n}`)}</li>)}
          </ol>
          <div className="tb-esim-codes">
            {qr && <img src={qr} alt={t('esim.qrAlt')} width="180" height="180" />}
            <div style={{ display: 'grid', gap: 8, minWidth: 0 }}>
              <p className="tb-note" style={{ margin: 0 }}>{t('esim.qrNote')}</p>
              <dl className="tb-facts">
                <dt>{t('esim.smdp')}</dt><dd><Ltr>{esim.smdp}</Ltr></dd>
                <dt>{t('esim.code')}</dt><dd><Ltr>{reveal ? esim.code : '••••-••••-••••'}</Ltr></dd>
                <dt>ICCID</dt><dd><Ltr>{esim.iccid}</Ltr></dd>
              </dl>
              <div className="tb-row">
                <button className="tb-ghost" onClick={() => setReveal((v) => !v)}>{reveal ? t('esim.hide') : t('esim.show')}</button>
                <button className="tb-ghost" onClick={() => copy('lpa', esim.lpa)}>{copied === 'lpa' ? '✓' : t('esim.copyCode')}</button>
                {device !== 'ios' && <button className="tb-ghost" onClick={() => copy('link', iosLink)}>{copied === 'link' ? '✓' : t('esim.copyLink')}</button>}
              </div>
            </div>
          </div>
          <p className="tb-note">{t('esim.dontDelete')}</p>
          <button className="tb-gold" onClick={() => set({ status: 'installed', installedAt: new Date().toISOString() })}>{t('esim.installed')}</button>
        </div>
      )}

      {esim.status === 'installed' && (
        <div className="tb-panel">
          <strong>{t('esim.readyArrival')}</strong>
          <p className="tb-note">{t('esim.arrivalHow')}</p>
          <button className="tb-btn" onClick={activate}>🛬 {t('esim.simArrive')}</button>
        </div>
      )}

      {(esim.status === 'active' || esim.status === 'exhausted') && (
        <div className="tb-panel">
          <div className="tb-row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <strong style={{ fontSize: 22 }}>{t('esim.left', { gb: formatNumber(Math.max(0, Math.round((total - used) * 10) / 10)) })}</strong>
            <span className="tb-note">{t('esim.ofTotal', { used: formatNumber(used), total: formatNumber(total) })}</span>
          </div>
          <div className="tb-meter" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${pct}%` }} className={pct >= 95 ? 'bad' : pct >= 80 ? 'mid' : ''} />
          </div>
          <p className="tb-note">{t('esim.expires', { when: formatDateTime(esim.expiresAt) })} · {t('esim.synced', { when: formatDateTime(esim.syncedAt) })}</p>
          {pct >= 80 && pct < 100 && <div className="tb-warn">{t(pct >= 95 ? 'esim.low95' : 'esim.low80')}</div>}
          {esim.status === 'exhausted' && <div className="tb-err">{t('esim.usedUp')}</div>}
          <div className="tb-row" style={{ marginTop: 12 }}>
            <button className="tb-ghost" onClick={useData} disabled={esim.status === 'exhausted'}>{t('esim.simUse')}</button>
            {(billing?.esim.topups || []).map((x) => (
              <button key={x.gb} className="tb-btn" disabled={!payReady} onClick={() => topUp(x.gb, cents(x.cents))}>{t('esim.topup', { gb: x.gb, price: cents(x.cents) })}</button>
            ))}
          </div>
          {error && <div className="tb-err" role="alert">{error}</div>}
          <details style={{ marginTop: 14 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{t('esim.troubleTitle')}</summary>
            <ol className="tb-howto">{[1, 2, 3, 4, 5].map((n) => <li key={n}>{t(`esim.trouble${n}`)}</li>)}</ol>
          </details>
        </div>
      )}

      {esim.status !== 'none' && (
        <button className="tb-link" style={{ marginTop: 12 }} onClick={() => window.confirm(t('esim.resetConfirm')) && update({ esim: { status: 'none' } })}>{t('esim.reset')}</button>
      )}
    </Section>
  );
}

/* ---------- Dialer: call trip places yourself ---------- */

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
const placeKey = (name) => String(name || '').replace(/\(.*?\)/g, '').trim().toLowerCase();
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/** Callable places from the trip: hotel, non-flight bookings, itinerary stops (deduped by name). */
function tripPlaces(state, found) {
  const { trip, bookings, itinerary } = state;
  const list = [];
  const add = (p) => {
    const key = placeKey(p.name);
    if (!key || list.some((x) => x.key === key || x.key.includes(key) || key.includes(x.key))) return;
    list.push({ ...p, key });
  };
  const bookingFor = (name) => bookings.find((b) => placeKey(b.name).includes(placeKey(name)) || placeKey(name).includes(placeKey(b.name)));
  if (trip.hotelName) add({ name: trip.hotelName, kind: 'hotel', phone: trip.hotelPhone, booking: bookings.find((b) => b.type === 'hotel') || null });
  bookings.filter((b) => b.type !== 'flight').forEach((b) => add({ name: b.name, kind: b.type, phone: b.phone, booking: b, when: b.start }));
  (itinerary?.days || []).forEach((d) => (d.items || []).forEach((i) => {
    if (i.place && !/^(walk|taxi|kix|hotel rest)$/i.test(i.place)) add({ name: i.place, kind: 'stop', phone: '', booking: bookingFor(i.place) || null, when: `${d.date}T${i.time || '00:00'}`, title: i.title });
  }));
  return list.slice(0, 20).map((p) => ({ ...p, phone: p.phone || found[p.key]?.phone || '', source: found[p.key] || null }));
}

/** The in-app phone (its own number, P3_PHONE_NUMBER). P3App keeps one for the whole app so calls survive tab changes. */
export function useP3Phone(state, meta, updateWith) {
  const [on, setOn] = useState(false);
  const [callerId, setCallerId] = useState('');
  const travelerId = state.trip.travelerId;
  const phone = useDialer({
    enabled: on,
    fetchToken: async () => {
      const data = await postJson(api('dialer/token'), { travelerId });
      setCallerId(data.callerId || '');
      return data;
    },
    heartbeat: () => postJson(api('dialer/heartbeat'), { travelerId }),
    placeCall: (to) => postJson(api('dialer/call'), { travelerId, to }),
    onEnded: (c) => {
      updateWith((s) => ({ phoneLog: [{ ...c, at: new Date(c.at).toISOString() }, ...(s.phoneLog || [])].slice(0, 50) }));
      postJson(api('phone/log'), { travelerId, tripId: state.trip.tripId, direction: c.direction, number: c.number, label: c.label || '', outcome: c.outcome, durationSec: c.duration, at: new Date(c.at).toISOString() }).catch(() => {});
    },
  });
  return { ...phone, on, turnOn: () => setOn(true), callerId };
}

/** Calls made and received on the in-app phone, newest first, with redial. */
function PhoneLog({ phone, log = [] }) {
  if (!log.length) return <p className="tb-note">{t('dialer.noHistory')}</p>;
  return log.map((h) => (
    <div key={h.id} className="tb-item" style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
      <span>
        <span aria-label={t(h.direction === 'in' ? 'dialer.in' : 'dialer.out')}>{h.direction === 'in' ? '↙' : '↗'}</span>{' '}
        <strong>{h.label || <Ltr>{h.number}</Ltr>}</strong>
        <span className="tb-note"> · {t(`dialer.outcome.${h.outcome}`)} · {mmss(h.duration)} · {formatDateTime(h.at)}</span>
      </span>
      <button className="tb-ghost" disabled={phone.status !== 'ready' || !!phone.call} onClick={() => phone.dial(h.number, h.label)}>{t('dialer.redial')}</button>
    </div>
  ));
}

/** Places on the trip with their numbers, and call prep (booking facts, language, phrases) for the one picked. */
export function TripPlaces({ ctx, state, onPick }) {
  const [found, setFound] = useState({});
  const [finding, setFinding] = useState('');
  const [selected, setSelected] = useState(null);
  const [prep, setPrep] = useState({});
  const { run, busy: prepping, error: prepError } = useTool(ctx);

  const places = tripPlaces(state, found);
  const place = places.find((p) => p.key === selected) || null;
  const p = place && prep[place.key];

  const find = async (pl) => {
    setFinding(pl.key);
    try {
      const near = state.trip.area ? `${state.trip.area}, ${state.trip.destination}` : state.trip.destination;
      const data = await postJson(api('lookup-number'), { businessName: pl.name.replace(/\(.*?\)/g, '').trim(), near });
      setFound((f) => ({ ...f, [pl.key]: data.found || { none: true } }));
      if (data.found && selected === pl.key) onPick(data.found.phone, pl.name);
    } catch {
      setFound((f) => ({ ...f, [pl.key]: { none: true } }));
    } finally {
      setFinding('');
    }
  };

  const choose = async (pl) => {
    setSelected(pl.key);
    onPick(pl.phone || '', pl.name);
    if (prep[pl.key]) return;
    const b = pl.booking;
    const r = await run('callPrep', {
      place: pl.name,
      kind: pl.kind,
      phone: pl.phone,
      country: state.trip.destination,
      purpose: b ? 'a question about my reservation' : 'a general question',
      booking: b ? { name: state.trip.travelerName, when: b.start, reference: b.confirmation, party: b.party || state.prefs.party, room: pl.kind === 'hotel' ? state.trip.roomNumber : '' } : null,
    });
    if (r) setPrep((m) => ({ ...m, [pl.key]: r }));
  };

  return (
    <>
      <div className="tb-sub">{t('dialer.places')}</div>
      {places.length === 0 && <p className="tb-note">{t('dialer.noPlaces')}</p>}
      <div className="tb-places">
        {places.map((pl) => (
          <div key={pl.key} className="tb-place" aria-current={pl.key === selected ? 'true' : undefined}>
            <button className="tb-place-main" onClick={() => choose(pl)}>
              <strong>{pl.name}</strong>
              <small>{join([pl.kind === 'stop' ? t('dialer.kindStop') : label('bookingType', pl.kind), pl.when && formatDateTime(pl.when), pl.title].filter(Boolean))}</small>
              <small>{pl.phone ? <Ltr>{pl.phone}</Ltr> : found[pl.key]?.none ? t('dialer.noNumber') : ''}</small>
            </button>
            {!pl.phone && !found[pl.key]?.none && (
              <button className="tb-link" onClick={() => find(pl)} disabled={finding === pl.key}>{finding === pl.key ? t('dialer.finding') : t('dialer.findNumber')}</button>
            )}
          </div>
        ))}
      </div>

      {place && (
        <div className="tb-panel">
          <span className="tb-kicker">{t('dialer.prep')}</span>
          <h4 className="tb-prep-title">{place.name}</h4>
          {place.source?.source && (
            <p className="tb-note">{t('call.found', { name: place.source.name || place.name })} · <a href={place.source.source} target="_blank" rel="noopener noreferrer">{t('call.source')}</a> · {t('call.checkNumber')}</p>
          )}

          <div className="tb-sub">{t('dialer.reservation')}</div>
          {place.booking ? (
            <dl className="tb-facts">
              {state.trip.travelerName && <><dt>{t('dialer.underName')}</dt><dd>{state.trip.travelerName}</dd></>}
              {place.booking.start && <><dt>{t('dialer.when')}</dt><dd>{formatDateTime(place.booking.start)}</dd></>}
              {place.booking.confirmation && <><dt>{t('dialer.ref')}</dt><dd><Ltr>{place.booking.confirmation}</Ltr></dd></>}
              {(place.booking.party || state.prefs.party) && <><dt>{t('dialer.party')}</dt><dd>{place.booking.party || state.prefs.party}</dd></>}
              {place.kind === 'hotel' && state.trip.roomNumber && <><dt>{t('trip.room')}</dt><dd><Ltr>{state.trip.roomNumber}</Ltr></dd></>}
            </dl>
          ) : (
            <p className="tb-note">{t('dialer.noBooking')}</p>
          )}

          <div className="tb-sub">{t('dialer.language')}</div>
          {prepping && !p && <p className="tb-note">{t('dialer.preparing')}</p>}
          {prepError && !p && <div className="tb-err">{prepError}</div>}
          {p && (
            <>
              <p style={{ margin: '0 0 6px' }}>
                {t('dialer.languageEst', { language: p.language })}{' '}
                <span className={`tb-badge ${p.englishLikely === 'high' ? 'done' : p.englishLikely === 'medium' ? 'partial' : 'missing'}`} style={{ display: 'inline-block', padding: '2px 10px' }}>
                  {t(`dialer.english.${['high', 'medium', 'low'].includes(p.englishLikely) ? p.englishLikely : 'medium'}`)}
                </span>
              </p>
              <p className="tb-note" style={{ margin: 0 }}>{p.englishNote} {t('dialer.estimate')}</p>
              {p.tips?.length > 0 && <ul className="tb-tips">{p.tips.map((tip, i) => <li key={i}>{tip}</li>)}</ul>}
              <div className="tb-sub">{t('dialer.phrases')}</div>
              {(p.phrases || []).map((ph, i) => (
                <div key={i} className="tb-item" style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div className="tb-big" style={{ fontSize: 18 }} lang={p.languageCode} dir="auto">{ph.local}</div>
                    {ph.romanized && <div className="tb-note" lang="en" dir="ltr">{ph.romanized}</div>}
                    <div className="tb-note">{ph.meaning}</div>
                  </div>
                  <button className="tb-ghost tb-round" aria-label={t('common.play', { text: ph.local })} onClick={() => speak(ph.local, p.languageCode)}>🔊</button>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </>
  );
}

function DialerSection({ ctx, state, phone: shared, hideLog = false }) {
  const { meta, updateWith } = useContext(Bench);
  const own = useP3Phone(state, meta, updateWith);
  const phone = shared || own;
  const [number, setNumber] = useState('');
  const [picked, setPicked] = useState({ number: '', name: '' });
  const [showKeys, setShowKeys] = useState(false);
  const inCall = phone.call && phone.call.state !== 'incoming';
  const press = (k) => (inCall ? phone.sendDigits(k) : setNumber((n) => n + k));
  const pick = (num, name) => {
    setNumber(num);
    setPicked({ number: num, name });
  };

  return (
    <Section id="dialer" kicker={t('kicker.calls')} desc={t('dialer.desc')}>
      <div className="tb-row" style={{ alignItems: 'center' }}>
        <span className={`tb-dot ${phone.status}`} aria-hidden="true" />
        <span className="tb-note" style={{ flex: 1 }}>
          {phone.status === 'ready' ? t('dialer.ready', { number: phone.callerId }) : phone.status === 'connecting' ? t('dialer.starting') : t('dialer.micNote')}
        </span>
        {!phone.on && <button className="tb-btn" onClick={phone.turnOn}>{t('dialer.start')}</button>}
      </div>
      {phone.error && <div className="tb-err" role="alert">{phone.error}</div>}

      {phone.call?.state === 'incoming' && (
        <div className="tb-callbar ring" role="alert">
          <div><strong>{t('dialer.incoming')}</strong><div><Ltr>{phone.call.number}</Ltr></div></div>
          <div className="tb-row">
            <button className="tb-btn tb-ok-btn" onClick={phone.answer}>{t('dialer.answer')}</button>
            <button className="tb-btn tb-bad-btn" onClick={phone.decline}>{t('dialer.decline')}</button>
          </div>
        </div>
      )}

      {inCall && (
        <div className="tb-callbar" role="status">
          <div>
            <strong>{phone.call.label || <Ltr>{phone.call.number}</Ltr>}</strong>
            <div className="tb-note">
              {phone.call.state === 'active' ? <>{!phone.muted && <span className="tb-live" aria-hidden="true" />}{t('dialer.connected', { time: mmss(phone.seconds) })}</> : phone.call.state === 'ringing' ? t('dialer.ringing') : t('dialer.calling')}
            </div>
          </div>
          <div className="tb-row">
            <button className="tb-ghost" aria-pressed={phone.muted} onClick={phone.toggleMute}>{phone.muted ? t('dialer.unmute') : t('dialer.mute')}</button>
            <button className="tb-ghost" aria-pressed={showKeys} onClick={() => setShowKeys((v) => !v)}>{t('dialer.keypad')}</button>
            {phone.outputs.length > 1 && (
              <select className="tb-lang" aria-label={t('dialer.speaker')} value={phone.outputId} onChange={(e) => phone.setOutput(e.target.value)}>
                {phone.outputs.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            )}
            <button className="tb-btn tb-bad-btn" onClick={phone.hangUp}>{t('dialer.hangUp')}</button>
          </div>
        </div>
      )}

      <TripPlaces ctx={ctx} state={state} onPick={pick} />

      <div className="tb-row" style={{ marginTop: 14 }}>
        <Field id="dial-number" label={t('dialer.number')} value={number} onChange={setNumber} placeholder="+81 75 …" dir="ltr" inputMode="tel" />
        <button className="tb-gold" disabled={phone.status !== 'ready' || !!phone.call || !number.trim()} onClick={() => phone.dial(number.trim(), number === picked.number ? picked.name : '')}>
          📞 {t('dialer.call')}
        </button>
        <button className="tb-ghost" aria-pressed={showKeys} onClick={() => setShowKeys((v) => !v)}>{t('dialer.keypad')}</button>
      </div>
      {showKeys && (
        <div className="tb-keypad" dir="ltr">
          {KEYS.map((k) => <button key={k} className="tb-key" onClick={() => press(k)}>{k}</button>)}
          {!inCall && <button className="tb-key" onClick={() => setNumber((n) => (n ? n.slice(0, -1) : '+'))} aria-label="⌫">⌫</button>}
        </div>
      )}

      {!hideLog && (
        <>
          <div className="tb-sub">{t('dialer.history')}</div>
          <PhoneLog phone={phone} log={state.phoneLog} />
        </>
      )}
    </Section>
  );
}

/* ---------- Today ---------- */

const DAY_MS = 86400000;
const isoToday = () => new Date().toLocaleDateString('en-CA');

/** Where the trip is today: { kind: 'day', n, total } | { kind: 'toGo', count } | { kind: 'done' } | null. */
function tripDay(trip) {
  if (!trip.startDate) return null;
  const start = new Date(`${trip.startDate}T00:00`);
  const end = new Date(`${trip.endDate || trip.startDate}T00:00`);
  const today = new Date(`${isoToday()}T00:00`);
  const total = Math.round((end - start) / DAY_MS) + 1;
  const n = Math.round((today - start) / DAY_MS) + 1;
  if (n < 1) return { kind: 'toGo', count: 1 - n };
  if (n > total) return { kind: 'done' };
  return { kind: 'day', n, total };
}

/** Trip day as text; tr = t for the UI, tEn for the AI prompt. */
function tripDayText(day, tr = t) {
  if (!day) return null;
  if (day.kind === 'toGo') return tr('today.daysToGo', { count: day.count });
  if (day.kind === 'done') return tr('today.tripDone');
  return tr('today.dayOf', { n: day.n, total: day.total });
}

/** "Today", "Tomorrow", or a short date, in the UI language (or English for the AI prompt). */
function friendlyDate(iso, loc) {
  const diff = Math.round((new Date(`${iso}T00:00`) - new Date(`${isoToday()}T00:00`)) / DAY_MS);
  return relativeDay(diff, iso, loc);
}

/** The next saved itinerary stop from now on, if any. */
function nextStop(itinerary) {
  const today = isoToday();
  const now = new Date().toTimeString().slice(0, 5);
  for (const day of itinerary?.days || []) {
    if (day.date < today) continue;
    const items = day.items || [];
    const item = day.date === today ? items.find((i) => (i.time || '') >= now) : items[0];
    if (item) return { ...item, date: day.date };
  }
  return null;
}

/** Small form: your name and (optional) phone number, then fills the whole sample trip. */
function SampleFiller({ state, update, onDone }) {
  const [name, setName] = useState(state.trip.travelerName || 'Allen');
  const [phone, setPhone] = useState('');
  const fill = () => {
    const hasData = state.trip.destination || state.bookings.length || state.itinerary;
    if (hasData && !window.confirm(t('trip.confirmSample'))) return;
    update(sampleTrip({ name, phone: phone.trim() }));
    onDone?.();
  };
  return (
    <div className="tb-panel" style={{ marginTop: 0, marginBottom: 14 }}>
      <span className="tb-kicker">✨ {t('sample.title')}</span>
      <p className="tb-note" style={{ margin: '4px 0 0' }}>{t('sample.desc')}</p>
      <div className="tb-row">
        <Field id="s-name" label={t('sample.name')} value={name} onChange={setName} />
        <Field id="s-phone" label={t('sample.phone')} value={phone} onChange={setPhone} placeholder="+1 …" dir="ltr" inputMode="tel" />
      </div>
      <p className="tb-note">{t('sample.phoneHint')}</p>
      <div className="tb-row">
        <button className="tb-gold" onClick={fill}>{t('sample.fill')}</button>
        {onDone && <button className="tb-ghost" onClick={onDone}>{t('common.cancel')}</button>}
      </div>
    </div>
  );
}

/** "Agent: …" / "User: …" transcript lines → [{ milo, text }]; system lines are dropped. */
const transcriptLines = (text = '') => String(text).split('\n').map((l) => l.match(/^\s*(AI|Agent|Assistant|Milo|User|Caller|Customer|Callee)\s*:\s*(.+)$/i))
  .filter(Boolean).map((m) => ({ milo: /^(ai|agent|assistant|milo)$/i.test(m[1]), text: m[2] }));
const minSec = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '');

const CHANGE_ICON = {
  reservation_booked: '✅', reservation_changed: '🔁', reservation_cancelled: '✖', checkout_changed: '🕑', checkin_changed: '🕑',
  plans_changed: '🗓', request_arranged: '🛎', info_received: 'ℹ', no_change: '–',
};

const NOTICE_ICON = { confirmation: '✅', reminder: '⏰', change: '🔁', cancellation: '✖', question: '❓', other: '💬' };

/**
 * Milo's feed for a traveler: calls Milo placed and texts businesses sent Milo, newest first.
 * Refreshes every 10 s so call results and new texts show up on their own.
 */
// ponytail: polling; switch to the live-transcript socket or push if the feed grows.
function useMiloFeed(travelerId) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const res = await fetch(`${api('conversations')}?channel=call,sms,search&limit=40&travelerId=${encodeURIComponent(travelerId)}`, { headers: authHeaders() });
      const data = await res.json().catch(() => null);
      signInAgainIf(res, data);
      if (!res.ok || !data?.success) throw new Error(data?.message || t('common.requestFailed', { status: String(res.status) }));
      setItems(data.conversations.filter((c) => (c.channel === 'sms' ? c.sms?.notification : c.channel === 'search' ? c.search : c.call?.callSid)));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [travelerId]);
  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    window.addEventListener(CALLS_CHANGED, load);
    return () => {
      clearInterval(id);
      window.removeEventListener(CALLS_CHANGED, load);
    };
  }, [load]);
  return { items, error, load };
}

/** The trip change Milo read from a business's text: before → after, with Update / Ignore. */
function TripUpdate({ c }) {
  const { state, updateWith } = useContext(Bench);
  const u = c.sms?.notification?.update;
  if (!u || !['add', 'change', 'cancel'].includes(u.action)) return null;
  const decided = (state.notices || {})[c.conversationId];
  if (decided) return <div className="tb-note">{decided === 'applied' ? t('update.applied') : t('update.ignored')}</div>;
  const p = proposeUpdate(state, u, c.sms.from);
  if (!p) return u.action === 'add' ? null : <div className="tb-note">{t('update.noMatch')}</div>;
  const decide = (status) => updateWith((prev) => {
    const notices = { ...(prev.notices || {}), [c.conversationId]: status };
    if (status !== 'applied') return { notices };
    const fresh = proposeUpdate(prev, u, c.sms.from);
    return fresh ? { notices, bookings: fresh.bookings, itinerary: fresh.itinerary } : { notices };
  });
  const when = (v) => (v ? formatDateTime(v) : '—');
  const hotel = p.after.type === 'hotel';
  const rows = p.action === 'change'
    ? [
      p.after.start !== p.before.start && [hotel ? t('update.checkin') : t('update.time'), p.before.start, p.after.start],
      p.after.end !== p.before.end && [hotel ? t('update.checkout') : t('update.ends'), p.before.end, p.after.end],
      p.after.party !== p.before.party && [t('update.party'), null, null, p.before.party, p.after.party],
    ].filter(Boolean)
    : p.action === 'add' ? [[hotel ? t('update.checkin') : t('update.time'), null, p.after.start]] : [];
  return (
    <div className="tb-update">
      <strong>{t(`update.${p.action}`, { name: (p.before || p.after).name.replace(/\s*\(sample\)/, '') })}</strong>
      {rows.map(([name, from, to, rawFrom, rawTo]) => (
        <div key={name} className="tb-update-row">
          <span>{name}</span>
          {(from || rawFrom) && <s>{rawFrom ?? when(from)}</s>}
          {(from || rawFrom) && <span aria-hidden="true">→</span>}
          <b>{rawTo ?? when(to)}</b>
        </div>
      ))}
      {p.item && (
        <div className="tb-update-row">
          <span>{t('update.itinerary')}</span>
          <span>{p.item.title}</span>
          {p.item.from && <s>{when(p.item.from)}</s>}
          {p.item.to ? <><span aria-hidden="true">→</span><b>{when(p.item.to)}</b></> : <b>{t('update.cancelledShort')}</b>}
        </div>
      )}
      <div className="tb-row" style={{ marginTop: 8 }}>
        <button className="tb-gold" onClick={() => decide('applied')}>{t('update.apply')}</button>
        <button className="tb-ghost" onClick={() => decide('ignored')}>{t('update.ignore')}</button>
      </div>
    </div>
  );
}

/** One line for a feed item in the bell: icon + headline. */
const feedLine = (c) => {
  if (c.channel === 'sms') return { icon: NOTICE_ICON[c.sms.notification.kind] || '💬', title: c.sms.notification.headline || c.sms.notification.summary, who: c.sms.notification.business || c.sms.from, at: c.sms.receivedAt || c.createdAt };
  if (c.channel === 'search') {
    const r = c.search.result || {};
    const title = c.search.status === 'failed' ? t('search.failed', { error: '' })
      : c.search.kind === 'flights' ? t('search.flightsReady', { route: r.from && r.to ? `${r.from} → ${r.to}` : '' }) : r.headline || r.summary || '';
    return { icon: c.search.kind === 'flights' ? '✈' : '🔎', title, who: t('search.label'), at: c.search.finishedAt || c.updatedAt };
  }
  return { icon: CHANGE_ICON[c.call.outcome?.change?.type] || '📞', title: c.call.outcome?.change?.headline || c.call.outcome?.summary || '', who: c.call.businessName || c.call.to, at: c.call.outcome?.analyzedAt || c.updatedAt || c.createdAt };
};
const finished = (c) => (c.channel === 'sms' && c.sms.notification.sender !== 'traveler') || (c.channel === 'search' ? c.search.status !== 'running' : Boolean(c.call?.outcome?.analyzedAt));

/** Header bell: unread count, recent notifications, pending trip updates, optional browser alerts. */
function NotificationBell({ feed }) {
  const { state, updateWith } = useContext(Bench);
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState(typeof Notification === 'undefined' ? 'unsupported' : Notification.permission);
  const alerted = useRef(null);
  const items = (feed.items || []).filter(finished);
  const seenAt = state.seenAt ? new Date(state.seenAt) : new Date(0);
  const unread = items.filter((c) => new Date(feedLine(c).at) > seenAt);
  const pending = (c) => c.channel === 'sms' && !(state.notices || {})[c.conversationId] && proposeUpdate(state, c.sms.notification.update, c.sms.from);

  // Browser alert for each item that arrives while the app is open (not for history on first load).
  useEffect(() => {
    if (!feed.items) return;
    const ids = items.map((c) => c.conversationId);
    if (!alerted.current) {
      alerted.current = new Set(ids);
      return;
    }
    items.filter((c) => !alerted.current.has(c.conversationId)).forEach((c) => {
      alerted.current.add(c.conversationId);
      if (perm !== 'granted') return;
      const l = feedLine(c);
      try {
        new Notification(`${l.icon} ${l.who}`, { body: l.title, tag: c.conversationId });
      } catch {
        /* some browsers only allow notifications from a service worker */
      }
    });
  }, [feed.items]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = () => {
    if (!open) updateWith(() => ({ seenAt: new Date().toISOString() }));
    setOpen(!open);
  };
  const askAlerts = async () => {
    try {
      setPerm(await Notification.requestPermission());
    } catch {
      setPerm('denied');
    }
  };
  const sorted = [...items].sort((a, b) => Boolean(pending(b)) - Boolean(pending(a)));

  return (
    <div className="tb-bellwrap">
      <button className="tb-bell" aria-label={t('notify.title')} aria-expanded={open} onClick={toggle}>
        🔔{unread.length > 0 && <span className="tb-bell-n">{unread.length > 9 ? '9+' : unread.length}</span>}
      </button>
      {open && (
        <div className="tb-bellpop" role="dialog" aria-label={t('notify.title')}>
          <div className="tb-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <strong>{t('notify.title')}</strong>
            {perm === 'default' && <button className="tb-link" onClick={askAlerts}>{t('notify.alerts')}</button>}
            {perm === 'granted' && <span className="tb-note">{t('notify.alertsOn')}</span>}
          </div>
          {sorted.length === 0 && <p className="tb-note">{t('notify.empty')}</p>}
          {sorted.slice(0, 8).map((c) => {
            const l = feedLine(c);
            return (
              <button key={c.conversationId} className="tb-bellitem" onClick={() => { setOpen(false); jumpTo('results'); }}>
                <span aria-hidden="true">{l.icon}</span>
                <span style={{ minWidth: 0 }}>
                  <small>{join([l.who, formatDateTime(l.at)])}</small>
                  <strong>{l.title}</strong>
                  {pending(c) && <em>{t('notify.pending')}</em>}
                </span>
              </button>
            );
          })}
          <button className="tb-link" style={{ marginTop: 8 }} onClick={() => { setOpen(false); jumpTo('results'); }}>{t('notify.openAll')} <Arrow /></button>
        </div>
      )}
    </div>
  );
}

/** A text a business sent Milo, as a notification card. */
/** A text the traveler sent Milo: the request, then its search progress/results or a call to review. */
function SmsTask({ c }) {
  const { state } = useContext(Bench);
  const [review, setReview] = useState(false);
  const n = c.sms.notification;
  return (
    <article className="tb-callcard">
      <div className="tb-callcard-top">
        <div style={{ minWidth: 0 }}>
          <strong>📨 {t('sms.fromYou')}</strong>
          <div className="tb-note">{join([formatDateTime(c.sms.receivedAt || c.createdAt)])} · <Ltr>{c.sms.from}</Ltr></div>
        </div>
        <span className="tb-badge partial">{t('sms.kind.task')}</span>
      </div>
      <div className="tb-bubble tb-me" style={{ maxWidth: '100%', animation: 'none' }} dir="auto">{c.sms.body}</div>
      {n.taskId && <SearchJob id={n.taskId} />}
      {n.task?.kind === 'call' && (
        review
          ? <div className="tb-panel" style={{ marginTop: 0 }}><CallReview instructions={n.task.request} travelerName={state.trip?.travelerName} autoPlan /></div>
          : <button className="tb-gold" onClick={() => setReview(true)}>☎ {t('sms.reviewCall')}</button>
      )}
      {!n.taskId && n.task?.kind !== 'call' && <div className="tb-note">{n.summary}</div>}
    </article>
  );
}

function SmsNotice({ c }) {
  const n = c.sms.notification;
  if (n.sender === 'traveler') return <SmsTask c={c} />;
  const details = Object.entries(n.details || {}).filter(([, v]) => v);
  const tone = n.kind === 'cancellation' ? 'tb-err' : n.kind === 'confirmation' ? 'tb-ok' : 'tb-warn';
  return (
    <article className="tb-callcard">
      <div className="tb-callcard-top">
        <div style={{ minWidth: 0 }}>
          <strong>{n.business || <Ltr>{c.sms.from}</Ltr>}</strong>
          <div className="tb-note">💬 {join([t('sms.text'), formatDateTime(c.sms.receivedAt || c.createdAt)])} · <Ltr>{c.sms.from}</Ltr></div>
        </div>
        <span className={`tb-badge ${n.kind === 'cancellation' ? 'missing' : n.kind === 'confirmation' ? 'done' : 'partial'}`}>{label('sms.kind', n.kind)}</span>
      </div>
      <div className={`tb-change ${tone}`}>
        <span aria-hidden="true">{NOTICE_ICON[n.kind] || '💬'}</span>
        <div>
          <strong>{n.headline || n.summary}</strong>
          {n.headline && <div>{n.summary}</div>}
        </div>
      </div>
      {details.length > 0 && (
        <dl className="tb-facts">
          {details.map(([k, v]) => <React.Fragment key={k}><dt>{k === 'reference' ? t('results.reference') : label('detail', k)}</dt><dd>{v}</dd></React.Fragment>)}
        </dl>
      )}
      {n.action && <div className="tb-warn" style={{ margin: 0 }}>👉 {t('sms.todo', { text: n.action })}</div>}
      <TripUpdate c={c} />
      <details>
        <summary className="tb-label" style={{ cursor: 'pointer' }}>{t('sms.original')}</summary>
        <div className="tb-bubble tb-milo" style={{ maxWidth: '100%', animation: 'none', marginTop: 6 }} dir="auto">{c.sms.body}</div>
      </details>
    </article>
  );
}

/**
 * A call's speaker-labelled transcript. When the call wasn't in the app's language, opening it
 * fetches a line-by-line translation (saved on the server per language) shown under each original line.
 */
function CallTranscript({ c, lines }) {
  const k = c.call;
  const target = uiLanguage();
  const foreign = Boolean(k.language) && k.language.split('-')[0] !== locale().split('-')[0];
  const cached = k.translations?.[target];
  const [tr, setTr] = useState(null);
  const shown = Array.isArray(cached) && cached.length === lines.length ? cached : tr?.lines;

  const load = async (force = false) => {
    if (!foreign || !lines.length || (!force && (shown || tr))) return;
    setTr({ loading: true });
    try {
      const r = await postJson(api('call-translate'), { conversationId: c.conversationId, uiLanguage: target });
      setTr({ lines: r.lines });
    } catch (e) {
      setTr({ error: e.message });
    }
  };

  return (
    <details style={{ marginTop: 8 }} onToggle={(e) => e.currentTarget.open && load()}>
      <summary className="tb-label" style={{ cursor: 'pointer' }}>{t('results.transcript')}{lines.length ? ` (${formatNumber(lines.length)})` : ''}</summary>
      {lines.length === 0 && <p className="tb-note">{t('results.noTranscript')}</p>}
      {foreign && lines.length > 0 && (
        <p className="tb-note" style={{ margin: '4px 0' }}>
          {tr?.loading ? <><span className="tb-live" aria-hidden="true" />{t('results.translating')}</>
            : tr?.error ? <>{t('results.translateFailed')} <button className="tb-link" onClick={() => load(true)}>{t('results.retry')}</button></>
              : shown ? `🌐 ${t('results.translatedFrom', { language: languageName(k.language) })}` : null}
        </p>
      )}
      <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
        {lines.map((l, i) => (
          <div key={i} className={`tb-bubble ${l.milo ? 'tb-me' : 'tb-milo'}`} style={{ maxWidth: '85%', animation: 'none' }}>
            <small style={{ opacity: 0.7 }}>{l.milo ? 'Milo' : k.businessName || t('results.staff')}</small>
            <div dir="auto" lang={k.language}>{l.text}</div>
            {shown?.[i] && shown[i] !== l.text && <div className="tb-translated" dir="auto">{shown[i]}</div>}
          </div>
        ))}
      </div>
    </details>
  );
}

/** Milo's log: every call Milo placed and every text a business sent Milo, newest first. */
function MiloCallsSection({ feed }) {
  const { meta, addBooking } = useContext(Bench);
  const { items, error: feedError, load } = feed;
  const [busy, setBusy] = useState('');
  const [saved, setSaved] = useState({});
  const [checkError, setError] = useState('');
  const error = feedError || checkError;

  const pending = (c) => !c.call.outcome || c.call.outcome.outcome === 'in_progress';

  const check = async (c) => {
    setBusy(c.conversationId);
    try {
      await postJson(api('call-outcome'), { callSid: c.call.callSid, transcript: c.call.transcript || '', uiLanguage: uiLanguage(), conversationId: c.conversationId, meta });
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const save = (c) => {
    const o = c.call.outcome;
    addBooking({ type: o.booking.type || 'other', name: o.booking.name, start: o.booking.start || '', end: null, timezone: '', address: '', phone: c.call.to || '', confirmation: o.booking.confirmation || o.confirmationNumber || '', source: 'ai-call', confirmed: o.outcome === 'confirmed' });
    setSaved({ ...saved, [c.conversationId]: true });
  };

  return (
    <Section id="results" kicker={t('kicker.calls')} desc={t('results.desc')}>
      <div className="tb-row">
        <button className="tb-ghost" onClick={load}>↻ {t('log.refresh')}</button>
      </div>
      {error && <div className="tb-err" role="alert">{error}</div>}
      {items && items.length === 0 && <p className="tb-note">{t('results.empty')}</p>}
      {items?.map((c) => {
        if (c.channel === 'sms') return <SmsNotice key={c.conversationId} c={c} />;
        if (c.channel === 'search' && c.search.source === 'sms') return null;
        if (c.channel === 'search') {
          return (
            <article key={c.conversationId} className="tb-callcard">
              <div className="tb-callcard-top">
                <div style={{ minWidth: 0 }}>
                  <strong>{c.search.kind === 'flights' ? '✈' : '🔎'} {t('search.label')}</strong>
                  <div className="tb-note" dir="auto">“{c.search.request}” · {formatDateTime(c.search.startedAt || c.createdAt)}</div>
                </div>
              </div>
              <SearchResult c={c} />
            </article>
          );
        }
        const k = c.call;
        const o = k.outcome;
        const lines = transcriptLines(k.transcript);
        const details = Object.entries(o?.details || {}).filter(([, v]) => v);
        return (
          <article key={c.conversationId} className="tb-callcard">
            <div className="tb-callcard-top">
              <div style={{ minWidth: 0 }}>
                <strong>{k.businessName || <Ltr>{k.to}</Ltr>}</strong>
                {k.goal && <div className="tb-note">{k.goal}</div>}
                <div className="tb-note">{join([formatDateTime(k.startedAt || c.createdAt), languageName(k.language), minSec(k.durationSec)].filter(Boolean))} · <Ltr>{k.to}</Ltr></div>
              </div>
              {pending(c)
                ? <span className="tb-badge partial"><span className="tb-live" aria-hidden="true" />{t('results.calling')}</span>
                : <span className={`tb-badge ${o.outcome === 'confirmed' ? 'done' : 'partial'}`}>{label('outcome', o.outcome)}</span>}
            </div>
            {o && !pending(c) && (
              <>
                <div className={`tb-change ${OUTCOME_CLASS[o.outcome] || 'tb-warn'}`}>
                  <span aria-hidden="true">{CHANGE_ICON[o.change?.type] || '📞'}</span>
                  <div>
                    <small style={{ display: 'block', fontWeight: 700, opacity: 0.75 }}>{o.change?.type ? label('change', o.change.type) : label('outcome', o.outcome)}</small>
                    <strong>{o.change?.headline || o.summary}</strong>
                    {o.change?.headline && <div>{o.summary}</div>}
                  </div>
                </div>
                {(details.length > 0 || o.confirmationNumber) && (
                  <dl className="tb-facts">
                    {details.map(([dk, v]) => <React.Fragment key={dk}><dt>{label('detail', dk)}</dt><dd>{v}</dd></React.Fragment>)}
                    {o.confirmationNumber && <><dt>{t('results.reference')}</dt><dd><Ltr>{o.confirmationNumber}</Ltr></dd></>}
                  </dl>
                )}
                {o.nextStep && <p className="tb-note">{t('call.next', { text: o.nextStep })}</p>}
              </>
            )}
            <div className="tb-row" style={{ marginTop: 8 }}>
              {o?.booking && <button className="tb-gold" onClick={() => save(c)} disabled={saved[c.conversationId]}>{saved[c.conversationId] ? t('call.saved') : t('call.save')}</button>}
              {(pending(c) || o.outcome === 'uncertain') && (
                <button className="tb-ghost" onClick={() => check(c)} disabled={busy === c.conversationId}>{busy === c.conversationId ? t('call.reading') : t('call.check')}</button>
              )}
            </div>
            <CallTranscript c={c} lines={lines} />
            <details>
              <summary className="tb-label" style={{ cursor: 'pointer' }}>{t('results.asked')}</summary>
              {k.firstMessage && <p className="tb-note" dir="auto" lang={k.language}><strong>{t('call.opening')}:</strong> {k.firstMessage}</p>}
              {k.script && <div className="tb-mono" dir="auto" lang={k.language}>{k.script}</div>}
              {k.options && (
                <p className="tb-note">{join(Object.keys(CALL_OPTION_DEFAULTS).filter((x) => k.options[x]).map((x) => `✓ ${t(`call.opt.${x}`)}`))}</p>
              )}
            </details>
          </article>
        );
      })}
    </Section>
  );
}

function TodaySection({ state, openSample, feed }) {
  const { trip } = state;
  const latest = feed.items?.find((c) => ((c.channel === 'sms' && c.sms.notification.sender !== 'traveler') || (c.channel === 'search' && c.search.status === 'done')) && Date.now() - new Date(c.createdAt) < 3 * 86400000);
  const notice = latest && feedLine(latest);
  const [city, country] = (trip.destination || '').split(',').map((x) => x.trim());
  const day = tripDay(trip);
  const next = nextStop(state.itinerary);
  const name = trip.travelerName ? trip.travelerName.split(' ')[0] : '';
  const heroDate = trip.startDate && formatDate(day?.kind === 'day' ? Date.now() : trip.startDate, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <section id="today" data-spy className="tb-reveal" style={{ display: 'grid', gap: 12, scrollMarginTop: 84 }} aria-label={t('title.today')}>
      <div className="tb-milo-card">
        <div className="tb-milo-top">
          <div className="tb-avatar" aria-hidden="true">🦊</div>
          <div>
            <strong>{t('today.milo')}</strong>
            <div className="tb-note" style={{ fontSize: 12 }}>{t('today.companion')}</div>
          </div>
        </div>
        <p className="tb-greet">{name ? t('today.greet', { name }) : t('today.greetNoName')}</p>
        {notice && (
          <div className="tb-notice">
            <small>{notice.icon} {latest.channel === 'search' ? t('search.ready') : latest.sms.notification.business ? t('today.newFrom', { name: latest.sms.notification.business }) : t('today.newMessage')}</small>
            <strong>{notice.title}</strong>
            {latest.sms?.notification.action && <div className="tb-note">👉 {latest.sms.notification.action}</div>}
            <TripUpdate c={latest} />
            <button className="tb-link" style={{ marginTop: 6 }} onClick={() => jumpTo('results')}>{t('notify.openAll')} <Arrow /></button>
          </div>
        )}
        <div className="tb-eye">
          <small>{t('today.eye')}</small>
          {next ? (
            <>
              <strong>{next.title}</strong>
              <div className="tb-note">{join([friendlyDate(next.date), next.time, next.place].filter(Boolean))}</div>
              <button className="tb-link" style={{ marginTop: 8 }} onClick={() => jumpTo('plan')}>{t('today.seeNext')} <Arrow /></button>
            </>
          ) : (
            <>
              <strong>{trip.destination ? t('today.noPlan') : t('today.whereTo')}</strong>
              <button className="tb-link" style={{ marginTop: 6 }} onClick={() => jumpTo(trip.destination ? 'plan' : 'trip')}>
                {trip.destination ? t('today.planDays') : t('today.setUp')} <Arrow />
              </button>
              {!trip.destination && (
                <button className="tb-link" style={{ marginTop: 6, marginInlineStart: 14 }} onClick={openSample}>{t('today.loadSample')} <Arrow /></button>
              )}
            </>
          )}
        </div>
        <button className="tb-btn tb-talk" onClick={() => { jumpTo('chat'); document.getElementById('chat-in')?.focus({ preventScroll: true }); }}>{t('today.talk')} <Arrow /></button>
      </div>

      <div className="tb-quick">
        <button onClick={() => jumpTo('esim')}><small>{t('today.esim', { gb: state.esim?.gb || ESIM_PLAN.gb })} ·{t(`esim.status.${state.esim?.status || 'none'}`)}</small><strong>{t('today.getConnected')} <Arrow /></strong></button>
        <button onClick={() => jumpTo('number')}><small>{t('today.number')}</small><strong>{state.number ? <Ltr>{state.number.number}</Ltr> : t('today.stayReachable')} <Arrow /></strong></button>
      </div>

      <div className="tb-hero">
        <img src={photoFor(trip.destination)} alt={trip.destination ? city : t('today.planeAlt')} />
        {trip.area && <span className="tb-chip">📍 {trip.area}</span>}
        <div className="tb-hero-in">
          <small>{join([country || city || t('today.nextTrip'), tripDayText(day)].filter(Boolean))}</small>
          <h2>{city ? t('today.heroTitle', { city }) : t('today.heroEmpty')}</h2>
          <p>{join([state.prefs.party, heroDate].filter(Boolean)) || t('today.heroHint')}</p>
        </div>
      </div>
    </section>
  );
}

const TILES = [
  ['✈', 'plan', 'plan'],
  ['↻', 'adjust', 'plan'],
  ['文', 'translate', 'translate'],
  ['⌂', 'hotel', 'hotel'],
  ['☎', 'call', 'call'],
  ['⊕', 'local', 'local'],
  ['✓', 'ready', 'ready'],
  ['📶', 'esim', 'esim'],
  ['📞', 'number', 'number'],
  ['🍜', 'food', 'food'],
  ['📱', 'dialer', 'dialer'],
];

function CompanionTiles() {
  return (
    <div className="tb-card tb-reveal">
      <header>
        <h3 style={{ fontSize: 22 }}>{t('tiles.title')}</h3>
        <p>{t('tiles.desc')}</p>
      </header>
      <div className="tb-tiles">
        {TILES.map(([icon, key, target]) => (
          <button key={key} className="tb-tile" onClick={() => jumpTo(target)}>
            <span aria-hidden="true">{icon}</span>
            <strong>{t(`tile.${key}`)}</strong>
            <small>{t(`tile.${key}.sub`)}</small>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- page ---------- */

/** Trip state (saved in this browser), UI language, the shared context and Milo's feed. */
const TRIP_KEYS = ['trip', 'prefs', 'itinerary', 'itineraryHistory', 'bookings', 'calls', 'notices'];
const ACCOUNT_KEYS = ['esim', 'number', 'credits', 'seenAt'];
const SAVED_KEYS = ['esim', 'number', 'seenAt'];
const pickKeys = (obj, keys) => Object.fromEntries(keys.filter((k) => obj?.[k] !== undefined).map((k) => [k, obj[k]]));
const hasContent = (s) => Boolean(s.trip.destination || s.bookings?.length || s.itinerary);
const todayIso = () => new Date().toLocaleDateString('en-CA');
const tripPath = (id) => api(`trips/${encodeURIComponent(id)}`);
/** A trip as the server stores it, merged over the blank fields. */
const tripFrom = (remote) => ({ ...pickKeys(EMPTY, TRIP_KEYS), ...pickKeys(remote, TRIP_KEYS) });
/** Server phone log → the app's call list and sent-texts list. */
const phoneLogsFrom = (entries = []) => ({
  phoneLog: entries.filter((e) => e.kind === 'call').map((e) => ({ id: e.entryId, direction: e.direction, number: e.number, label: e.label, outcome: e.outcome, duration: e.durationSec || 0, at: e.at })),
  textLog: entries.filter((e) => e.kind === 'text').map((e) => ({ id: e.entryId, direction: e.direction, to: e.number, body: e.body, status: e.status, at: e.at })),
});

/** The guest id this browser has used, so signing in can move its history to the account. */
export function guestTravelerId() {
  try {
    return JSON.parse(localStorage.getItem(STATE_KEY) || '{}').trip?.travelerId || '';
  } catch {
    return '';
  }
}

/** After log-in: this browser's saved state now belongs to the account (its id replaces the guest id). */
export function bindStateToAccount(accountId) {
  try {
    const saved = JSON.parse(localStorage.getItem(STATE_KEY) || '{}');
    localStorage.setItem(STATE_KEY, JSON.stringify({ ...saved, trip: { ...(saved.trip || {}), travelerId: accountId } }));
  } catch {
    /* storage is a convenience only */
  }
}

export function useBenchState() {
  const [state, setState] = useState(loadState);
  const [lang, setLang] = useState(locale());
  const [account, setAccount] = useState(null);
  const [trips, setTrips] = useState(null);
  const [syncError, setSyncError] = useState('');
  const [numbers, setNumbers] = useState({ milo: '', travel: '' });
  const accountRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const saved = useRef({ trip: '', account: '' });

  useEffect(() => {
    loadLocale(initialLocale()).then(setLang).catch(() => {});
  }, []);

  const changeLanguage = (code) => loadLocale(code).then(setLang).catch(() => {});

  const updateWith = (fn) =>
    setState((prev) => {
      const next = { ...prev, ...fn(prev) };
      const id = accountRef.current?.accountId;
      if (id && next.trip.travelerId !== id) next.trip = { ...next.trip, travelerId: id };
      try {
        localStorage.setItem(STATE_KEY, JSON.stringify(next));
      } catch {
        /* storage is a convenience only */
      }
      return next;
    });
  const update = (patch) => updateWith(() => patch);

  const loadTrips = useCallback(() => requestJson(api(`trips?today=${todayIso()}`)).then((d) => setTrips(d.trips)), []);

  const dropSession = useCallback(() => {
    setSessionToken('');
    accountRef.current = null;
    saved.current = { trip: '', account: '' };
    setAccount(null);
    setTrips(null);
  }, []);

  /** Uploads whatever changed since the last save: the open trip (once it has content) and the account state. */
  const flush = useCallback(async () => {
    if (!accountRef.current) return;
    const s = stateRef.current;
    const tripJson = JSON.stringify(pickKeys(s, TRIP_KEYS));
    if (hasContent(s) && tripJson !== saved.current.trip) {
      await requestJson(tripPath(s.trip.tripId), { method: 'PUT', body: JSON.parse(tripJson) });
      saved.current.trip = tripJson;
      loadTrips().catch(() => {});
    }
    const accountJson = JSON.stringify({ state: { ...pickKeys(s, SAVED_KEYS), lang: locale() }, activeTripId: s.trip.tripId });
    if (accountJson !== saved.current.account) {
      await requestJson(api('account/me'), { method: 'PUT', body: JSON.parse(accountJson) });
      saved.current.account = accountJson;
    }
  }, [loadTrips]);

  // Signed in: load the account, its trips and phone log, and open the active (or current, or next) trip.
  // A trip made as a guest in this browser is saved to the account first, so nothing is lost.
  useEffect(() => {
    if (!sessionToken()) {
      window.location.hash = '#/p3-login';
      return undefined;
    }
    let live = true;
    (async () => {
      try {
        const [{ account: a, numbers: owned = {} }, { trips: list }, { entries }] = await Promise.all([
          requestJson(api('account/me')),
          requestJson(api(`trips?today=${todayIso()}`)),
          requestJson(api('phone/log')),
        ]);
        const local = stateRef.current;
        if (hasContent(local) && !list.some((x) => x.tripId === local.trip.tripId)) {
          await requestJson(tripPath(local.trip.tripId), { method: 'PUT', body: pickKeys(local, TRIP_KEYS) });
          list.unshift({ tripId: local.trip.tripId });
        }
        const open = list.find((x) => x.tripId === a.activeTripId) || list.find((x) => x.status === 'current') || list.find((x) => x.status === 'upcoming') || list[0];
        const remote = open ? (await requestJson(tripPath(open.tripId))).trip : null;
        if (!live) return;
        if (!a.emailVerified) {
          window.location.hash = '#/p3-verify';
          return;
        }
        accountRef.current = a;
        if (remote) saved.current.trip = JSON.stringify(pickKeys(tripFrom(remote), TRIP_KEYS));
        if (a.state?.lang && a.state.lang !== locale()) changeLanguage(a.state.lang);
        updateWith((prev) => ({
          ...(remote ? tripFrom(remote) : {}),
          ...pickKeys(a.state, ACCOUNT_KEYS),
          ...phoneLogsFrom(entries),
          number: owned.travel ? { ...(prev.number?.number === owned.travel ? prev.number : {}), ...travelNumberState(owned.travel) } : null,
        }));
        setNumbers({ milo: owned.milo || '', travel: owned.travel || '' });
        setAccount(a);
        loadTrips().catch(() => {});
      } catch (e) {
        if (!live) return;
        if (e.status === 401) dropSession();
        else setSyncError(e.message);
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Texts and calls to the travel number arrive on the server: pull the phone log while signed in.
  useEffect(() => {
    if (!account) return undefined;
    const pull = () => requestJson(api('phone/log')).then(({ entries }) => updateWith(() => phoneLogsFrom(entries))).catch(() => {});
    const id = setInterval(pull, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  useEffect(() => {
    if (!account) return undefined;
    const timer = setTimeout(() => {
      flush()
        .then(() => setSyncError(''))
        .catch((e) => (e.status === 401 ? dropSession() : setSyncError(e.message)));
    }, 800);
    return () => clearTimeout(timer);
  }, [account, state, lang, flush, dropSession]);

  const openTrip = async (tripId) => {
    await flush().catch(() => {});
    const { trip: remote } = await requestJson(tripPath(tripId));
    saved.current.trip = JSON.stringify(pickKeys(tripFrom(remote), TRIP_KEYS));
    updateWith(() => tripFrom(remote));
  };
  const newTrip = async () => {
    await flush().catch(() => {});
    updateWith(() => pickKeys(freshState(), TRIP_KEYS));
  };
  const deleteTrip = async (tripId) => {
    await requestJson(tripPath(tripId), { method: 'DELETE' });
    setTrips((list) => (list || []).filter((x) => x.tripId !== tripId));
    if (stateRef.current.trip.tripId === tripId) {
      saved.current.trip = '';
      updateWith(() => pickKeys(freshState(), TRIP_KEYS));
    }
  };
  const signOut = async () => {
    await flush().catch(() => {});
    await postJson(api('account/logout'), {}).catch(() => {});
    dropSession();
    update(freshState());
    window.location.hash = '#/p3-login';
  };
  const accountChanged = (a) => {
    accountRef.current = a;
    setAccount(a);
  };

  const ctx = { trip: state.trip, preferences: state.prefs, bookings: state.bookings, itinerary: state.itinerary };
  const meta = metaOf(state);
  const feed = useMiloFeed(meta.traveler.id);
  const addBooking = (b) => updateWith((prev) => ({ bookings: [...prev.bookings, b] }));
  return {
    state, update, updateWith, lang, changeLanguage, ctx, meta, feed, addBooking,
    account, numbers, trips, syncError, openTrip, newTrip, deleteTrip, signOut, accountChanged,
    bench: { ctx, meta, addBooking, state, updateWith, feed },
  };
}

export default function P3TestBench() {
  const { state, update, lang, changeLanguage, ctx, feed, bench } = useBenchState();
  const [active, setActive] = useState('today');
  const [sampleOpen, setSampleOpen] = useState(false);
  const openSample = () => {
    setSampleOpen(true);
    setTimeout(() => jumpTo('trip'), 50);
  };

  useEffect(() => {
    const spy = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: '-30% 0px -60% 0px' },
    );
    document.querySelectorAll('[data-spy]').forEach((el) => spy.observe(el));
    return () => spy.disconnect();
  }, []);

  const group = groupOf(active);
  const groupHead = (gid) => (
    <div className="tb-group" id={`g-${gid}`}>
      <h2>{t(`group.${gid}`)}</h2>
      <p>{t(`group.${gid}.desc`)}</p>
    </div>
  );
  const link = (id) => (e) => {
    e.preventDefault();
    jumpTo(id);
  };

  return (
    <PayProvider>
    <Bench.Provider value={bench}>
      <div className="tb" dir={dir()} lang={lang}>
        <style>{CSS}</style>
        <div className="tb-layout">
          <nav className="tb-side" aria-label={t('nav.navigation')}>
            <div className="tb-brand">{t('app.brand')}</div>
            {GROUPS.map(([gid, , ids]) => (
              <React.Fragment key={gid}>
                <h4>{t(`group.${gid}`)}</h4>
                {ids.map((id) => (
                  <a key={id} href={`#${id}`} onClick={link(id)} aria-current={active === id ? 'true' : undefined}>{t(`title.${id}`)}</a>
                ))}
              </React.Fragment>
            ))}
          </nav>

          <div className="tb-phone">
            <header className="tb-head">
              <div>
                <div className="tb-k">{t('app.kicker')}</div>
                <h1><span key={`${group}-${lang}`} className="tb-title">{t(`group.${group}`)}</span></h1>
              </div>
              {state.trip.destination && <span className="tb-pill">✈ {state.trip.destination}</span>}
              <NotificationBell feed={feed} />
              <button className="tb-sample" onClick={openSample} title={t('sample.title')}>✨ <span>{t('sample.button')}</span></button>
              <select className="tb-lang" aria-label={t('app.language')} value={lang} onChange={(e) => changeLanguage(e.target.value)}>
                {LANGUAGES.map((l) => <option key={l.code} value={l.code} lang={l.code}>{l.native}</option>)}
              </select>
            </header>

            <main className="tb-body">
              <TodaySection state={state} openSample={openSample} feed={feed} />
              <div className="tb-warn" style={{ margin: 0 }}>{t('app.warning')}</div>

              {groupHead('companion')}
              <CompanionTiles />
              <ChatSection ctx={ctx} state={state} />

              {groupHead('mytrip')}
              <TripSection state={state} update={update} sampleOpen={sampleOpen} setSampleOpen={setSampleOpen} />
              <PlanSection ctx={ctx} state={state} update={update} />
              <BookingsSection ctx={ctx} state={state} update={update} />

              {groupHead('tools')}
              <ESimSection state={state} update={update} />
              <TravelNumberSection state={state} update={update} />
              <TranslateSection ctx={ctx} />
              <HotelSection ctx={ctx} state={state} />
              <LocalSection ctx={ctx} state={state} />
              <ReadySection ctx={ctx} />
              <FoodSection ctx={ctx} />

              {groupHead('calls')}
              <Section id="call" kicker={t('kicker.calls')} desc={t('call.desc')}>
                <CallReview travelerName={state.trip.travelerName} onPlaced={(c) => update({ calls: [c, ...state.calls].slice(0, 20) })} />
              </Section>
              <MiloCallsSection feed={feed} />
              <DialerSection ctx={ctx} state={state} />
              <StatusSection />
            </main>

            <nav className="tb-tabs" aria-label={t('nav.sections')}>
              {GROUPS.map(([gid, icon]) => (
                <button key={gid} aria-current={group === gid ? 'true' : undefined} onClick={() => jumpTo(gid === 'today' ? 'today' : `g-${gid}`)}>
                  <span aria-hidden="true">{icon}</span>
                  {t(`group.${gid}`)}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </Bench.Provider>
    </PayProvider>
  );
}

export {
  CSS, Bench, Arrow, Ltr, join, photoFor, nextStop, tripDay, tripDayText, friendlyDate, feedLine, ESIM_PLAN, NOTICE_ICON,
  TripSection, PlanSection, BookingsSection, ESimSection, TravelNumberSection, TranslateSection, HotelSection, LocalSection, ReadySection, FoodSection,
  DialerSection, PhoneLog, mmss, postJson, requestJson, api, Field, MiloCallsSection, NotificationBell, TripUpdate, RouteCard, jumpTo,
};
